import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  getThemes, createTheme, updateTheme, deleteTheme, uploadThemeImage,
  uploadThemeOverlay, removeThemeOverlay,
  getThemeAnimations, createThemeAnimation, updateThemeAnimation, deleteThemeAnimation,
  getThemeStreamers, getStreamers, assignTheme, unassignTheme,
  Theme, ThemeAnimation, ThemeStreamer, Streamer, StaticElement,
  getAnimations, uploadAnimation, getThemeAnimationFiles, uploadThemeAnimationFile, animationFileUrl, overlayUrl,
  getStaticElements, createStaticElement, updateStaticElement, deleteStaticElement,
  triggerManualAnimation as _triggerManualAnimation, // imported for completeness
  getElevenLabsVoices, ttsElevenLabs,
} from './api';

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

interface AnimFormState {
  name: string;
  animation_file: string;
  animation_type: 'video' | 'audio' | 'text' | 'text_dynamic' | 'gift_grid' | 'queue' | 'raffle' | 'mobile_comments' | 'banner';
  trigger_type: 'auto' | 'manual';
  text_content: string;
  text_color: string;
  text_size: number;
  text_effect: string;
  text_duration: number;
  text_font: string;
  text_loops: number;
  text_bg_color: string;
  text_bg_opacity: number;
  text_bg_anim: string;
  show_gift_image: boolean;
  gift_img_x: number;
  gift_img_y: number;
  gift_img_w: number;
  gift_img_h: number;
  gift_label_pos: 'none' | 'above' | 'below' | 'left' | 'right' | 'center';
  gift_label_color: string;
  gift_label_size: number;
  gift_cols: number;
  queue_max_visible: number;
  queue_show_avatar: boolean;
  queue_show_counter: boolean;
  queue_appear_anim: string;
  queue_disappear_anim: string;
  queue_auto_hide: number;
  queue_particle: string;
  queue_sound: string;
  queue_min_coins: number;
  queue_bg_image: string;
  queue_q_bg_image: string;
  queue_q_bg_color: string;
  queue_q_pos_x: number;
  queue_q_pos_y: number;
  queue_q_pos_w: number;
  queue_q_pos_h: number;
  queue_ctrl_pos_x: number;
  queue_ctrl_pos_y: number;
  queue_ctrl_pos_w: number;
  queue_ctrl_pos_h: number;
  queue_show_gift: boolean;
  queue_gift_pos_x: number;
  queue_gift_pos_y: number;
  queue_gift_pos_w: number;
  queue_gift_pos_h: number;
  queue_gift_label: string;
  queue_gift_label_pos: string;
  queue_gift_label_color: string;
  queue_gift_label_size: number;
  queue_show_pgift: boolean;
  queue_pgift_pos_x: number;
  queue_pgift_pos_y: number;
  queue_pgift_pos_w: number;
  queue_pgift_pos_h: number;
  queue_pgift_label: string;
  queue_pgift_label_pos: string;
  queue_pgift_label_color: string;
  queue_pgift_label_size: number;
  queue_tts_enabled: boolean;
  queue_tts_voice_id: string;
  mobile_comment_voice_id: string;
  mobile_comment_voice_name: string;
}
const emptyAnimForm = (): AnimFormState => ({
  name: '', animation_file: '',
  animation_type: 'video',
  trigger_type: 'auto',
  text_content: '', text_color: '#ffffff', text_size: 48, text_effect: 'fade', text_duration: 5,
  text_font: 'sans-serif', text_loops: 1,
  text_bg_color: '#000000', text_bg_opacity: 0, text_bg_anim: 'none',
  show_gift_image: false, gift_img_x: 5, gift_img_y: 5, gift_img_w: 20, gift_img_h: 20,
  gift_label_pos: 'none', gift_label_color: '#ffffff', gift_label_size: 12, gift_cols: 0,
  queue_max_visible: 5, queue_show_avatar: true, queue_show_counter: true,
  queue_appear_anim: 'zoom', queue_disappear_anim: 'fade', queue_auto_hide: 0,
  queue_particle: 'none', queue_sound: '', queue_min_coins: 0,
  queue_bg_image: '', queue_q_bg_image: '', queue_q_bg_color: '#111111',
  queue_q_pos_x: 15, queue_q_pos_y: 38, queue_q_pos_w: 70, queue_q_pos_h: 24,
  queue_ctrl_pos_x: 2, queue_ctrl_pos_y: 89, queue_ctrl_pos_w: 34, queue_ctrl_pos_h: 8,
  queue_show_gift: false, queue_gift_pos_x: 70, queue_gift_pos_y: 5, queue_gift_pos_w: 18, queue_gift_pos_h: 10,
  queue_gift_label: '', queue_gift_label_pos: 'none', queue_gift_label_color: '#ffffff', queue_gift_label_size: 14,
  queue_show_pgift: false, queue_pgift_pos_x: 70, queue_pgift_pos_y: 18, queue_pgift_pos_w: 18, queue_pgift_pos_h: 10,
  queue_pgift_label: '', queue_pgift_label_pos: 'none', queue_pgift_label_color: '#ffffff', queue_pgift_label_size: 14,
  queue_tts_enabled: false, queue_tts_voice_id: '',
  mobile_comment_voice_id: '', mobile_comment_voice_name: '',
});

const defaultBannerJson = () => JSON.stringify({
  patterns: [
    { text: 'Bienvenue sur le live', effect: 'scroll-left', color: '#3b82f6', font: 'sans-serif', size: 42, duration: 9000 },
    { text: 'Abonne-toi pour ne rien rater', effect: 'scroll-down', color: '#facc15', font: 'sans-serif', size: 42, duration: 8000 },
  ],
  bg_color: '#000000',
  bg_opacity: 0,
  layer: 'below_queue',
}, null, 2);

interface StaticFormState {
  name: string; image_file: string; pos_x: number; pos_y: number; pos_w: number; pos_h: number; layer: 'above' | 'below'; sort_order: number;
}
const emptyStaticForm = (): StaticFormState => ({ name: '', image_file: '', pos_x: 0, pos_y: 0, pos_w: 30, pos_h: 30, layer: 'above', sort_order: 0 });
interface ThemeFormState { name: string; slug: string; image_url: string; }
const emptyTheme = (): ThemeFormState => ({ name: '', slug: '', image_url: '' });

interface DragState {
  mode: 'move' | 'resize-br' | 'resize-bl' | 'resize-tr' | 'resize-tl';
  target: 'anim' | 'gift';
  boxKey?: string;  // fullscreen 3-panel drag: 'anim-{id}' or 'gift-{id}'
  startMX: number; startMY: number;
  startPos: { x: number; y: number; w: number; h: number };
}

interface Props { isAdmin: boolean; isMobile?: boolean; headerSlot?: React.ReactNode; }

export default function ThemeLibrary({ isAdmin, isMobile, headerSlot }: Props) {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [selected, setSelected] = useState<Theme | null>(null);
  const [search, setSearch] = useState('');
  const [animations, setAnimations] = useState<ThemeAnimation[]>([]);
  const [selectedAnim, setSelectedAnim] = useState<ThemeAnimation | null>(null);
  const [detailTab, setDetailTab] = useState<'animations' | 'static' | 'streamers'>('animations');
  const [staticElements, setStaticElements] = useState<StaticElement[]>([]);
  const [staticForm, setStaticForm] = useState<StaticFormState>(emptyStaticForm());
  const [editingStaticId, setEditingStaticId] = useState<number | null>(null);
  const [showStaticForm, setShowStaticForm] = useState(false);
  const [themeStreamers, setThemeStreamers] = useState<ThemeStreamer[]>([]);
  const [allStreamers, setAllStreamers] = useState<Streamer[]>([]);
  const [animFiles, setAnimFiles] = useState<string[]>([]);

  const [editPos, setEditPos] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [editGiftPos, setEditGiftPos] = useState<{ x: number; y: number; w: number; h: number }>({ x: 5, y: 5, w: 20, h: 20 });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSafeZone, setShowSafeZone] = useState(false);
  const [streamerSearch, setStreamerSearch] = useState('');
  const dragRef = useRef<DragState | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewRefFull = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [overlayUploading, setOverlayUploading] = useState(false);
  const [overlayMsg, setOverlayMsg] = useState('');
  const [overlayCacheBust, setOverlayCacheBust] = useState(0);
  const overlayInputRef = useRef<HTMLInputElement>(null);

  const [showThemeForm, setShowThemeForm] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [themeForm, setThemeForm] = useState<ThemeFormState>(emptyTheme());
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const imageRef = useRef<HTMLInputElement>(null);

  const [showAnimForm, setShowAnimForm] = useState(false);
  const [editingAnim, setEditingAnim] = useState<ThemeAnimation | null>(null);
  const [animForm, setAnimForm] = useState<AnimFormState>(emptyAnimForm());
  const [uploadingAnim, setUploadingAnim] = useState(false);
  const animFileRef = useRef<HTMLInputElement>(null);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const [animError, setAnimError] = useState('');
  // Fullscreen 3-panel state
  const [checkedAnimIds, setCheckedAnimIds] = useState<Set<number>>(new Set());
  const [checkedGiftIds, setCheckedGiftIds] = useState<Set<number>>(new Set());
  const [pendingPos, setPendingPos] = useState<Map<string, { x: number; y: number; w: number; h: number }>>(new Map());
  const [pendingDirty, setPendingDirty] = useState(false);
  const [savingPending, setSavingPending] = useState(false);
  const [refKey, setRefKey] = useState<string | null>(null);  // 'anim-{id}' ou 'gift-{id}'
  const [playingAnimId, setPlayingAnimId] = useState<number | null>(null);
  const [fullscreenMobileTab, setFullscreenMobileTab] = useState<'theme' | 'animations' | 'position'>('theme');
  const fullscreenVideoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());

  const loadAnimations = async (themeId: number) => {
    const res = await getThemeAnimations(themeId);
    setAnimations(res.data);
    // Dériver immédiatement les fichiers disponibles depuis les animations (fallback robuste)
    const fromAnims = Array.from(new Set(
      res.data.map((a: ThemeAnimation) => a.animation_file).filter((f): f is string => !!f && f !== '')
    ));
    setAnimFiles(fromAnims);
    // Compléter avec l'API thème (uploads récents non encore assignés)
    try {
      const fr = await getThemeAnimationFiles(themeId);
      setAnimFiles(Array.from(new Set([...fromAnims, ...fr.data.animations])));
    } catch {
      // endpoint non disponible (backend ancien) — on garde fromAnims
    }
  };
  const loadThemeStreamers = async (themeId: number) => {
    const res = await getThemeStreamers(themeId);
    setThemeStreamers(res.data);
  };
  const loadStaticElems = async (themeId: number) => {
    const res = await getStaticElements(themeId);
    setStaticElements(res.data);
  };
  // loadAnimFiles est maintenant intégré dans loadAnimations — gardé pour upload
  const loadAnimFiles = async (themeId?: number) => {
    if (!themeId) { setAnimFiles([]); return; }
    try {
      const res = await getThemeAnimationFiles(themeId);
      setAnimFiles(prev => Array.from(new Set([...prev, ...res.data.animations])));
    } catch {}
  };

  useEffect(() => {
    getThemes().then(r => setThemes(r.data));
    getStreamers().then(r => setAllStreamers(r.data));
  }, []);

  useEffect(() => {
    if (!selected) { setAnimations([]); setThemeStreamers([]); setStaticElements([]); setSelectedAnim(null); setAnimFiles([]); return; }
    loadAnimations(selected.id); // inclut loadAnimFiles automatiquement
    if (detailTab === 'streamers') loadThemeStreamers(selected.id);
    if (detailTab === 'static') loadStaticElems(selected.id);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selected && detailTab === 'streamers') loadThemeStreamers(selected.id);
    if (selected && detailTab === 'static') loadStaticElems(selected.id);
  }, [detailTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAnim) {
      const fresh = animations.find(a => a.id === selectedAnim.id);
      if (fresh) { setSelectedAnim(fresh); setEditPos({ x: fresh.pos_x, y: fresh.pos_y, w: fresh.pos_w, h: fresh.pos_h }); setIsDirty(false); }
    }
  }, [animations]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectAnim = (a: ThemeAnimation) => {
    setSelectedAnim(a);
    setEditPos({ x: a.pos_x, y: a.pos_y, w: a.pos_w, h: a.pos_h });
    setEditGiftPos({ x: a.gift_img_x ?? 5, y: a.gift_img_y ?? 5, w: a.gift_img_w ?? 20, h: a.gift_img_h ?? 20 });
    setIsDirty(false); setIsPlaying(false);
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; }
  };

  const startDrag = (e: React.MouseEvent, mode: DragState['mode'], target: DragState['target'] = 'anim') => {
    e.preventDefault(); e.stopPropagation();
    const pos = target === 'gift' ? editGiftPos : editPos;
    if (!pos) return;
    dragRef.current = { mode, target, startMX: e.clientX, startMY: e.clientY, startPos: { ...pos } };
  };

  const isDirtyRef = useRef(false);
  const editPosRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const selectedAnimRef = useRef<ThemeAnimation | null>(null);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { editPosRef.current = editPos; }, [editPos]);
  useEffect(() => { selectedAnimRef.current = selectedAnim; }, [selectedAnim]);
  const editGiftPosRef = useRef(editGiftPos);
  useEffect(() => { editGiftPosRef.current = editGiftPos; }, [editGiftPos]);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Ref vers savePosition pour éviter la closure périmée dans le useEffect mousemove
  const savePositionRef = useRef<(pos: { x: number; y: number; w: number; h: number }) => Promise<void>>(() => Promise.resolve());

  useEffect(() => {
    const calcPos = (d: DragState, dx: number, dy: number) => {
      const sp = d.startPos;
      let nx = sp.x, ny = sp.y, nw = sp.w, nh = sp.h;
      if (d.mode === 'move') { nx = sp.x + dx; ny = sp.y + dy; }
      else if (d.mode === 'resize-br') { nw = Math.max(5, sp.w + dx); nh = Math.max(5, sp.h + dy); }
      else if (d.mode === 'resize-bl') { const re = sp.x + sp.w; nx = sp.x + dx; nw = Math.max(5, re - nx); nh = Math.max(5, sp.h + dy); }
      else if (d.mode === 'resize-tr') { const be = sp.y + sp.h; ny = sp.y + dy; nh = Math.max(5, be - ny); nw = Math.max(5, sp.w + dx); }
      else if (d.mode === 'resize-tl') { const re = sp.x + sp.w; const be = sp.y + sp.h; nx = sp.x + dx; ny = sp.y + dy; nw = Math.max(5, re - nx); nh = Math.max(5, be - ny); }
      return { x: nx, y: ny, w: nw, h: nh };
    };
    const onMouseMove = (e: MouseEvent) => {
      const d = dragRef.current;
      const activeRef = previewRefFull.current || previewRef.current;
      if (!d || !activeRef) return;
      const rect = activeRef.getBoundingClientRect();
      const dx = (e.clientX - d.startMX) / rect.width * 100;
      const dy = (e.clientY - d.startMY) / rect.height * 100;
      const newPos = calcPos(d, dx, dy);
      if (d.boxKey) {
        // Fullscreen 3-panel drag → accumulate in pendingPos
        const key = d.boxKey;
        setPendingPos(prev => { const n = new Map(prev); n.set(key, newPos); return n; });
        setPendingDirty(true);
      } else {
        // Mini-canvas drag → update editPos / editGiftPos
        if (d.target === 'gift') {
          setEditGiftPos(newPos);
        } else {
          if (!editPosRef.current) return;
          setEditPos(newPos);
        }
        setIsDirty(true);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      const d = dragRef.current;
      const activeRef = previewRefFull.current || previewRef.current;
      const touch = e.touches[0];
      if (!d || !activeRef || !touch) return;
      e.preventDefault();
      const rect = activeRef.getBoundingClientRect();
      const dx = (touch.clientX - d.startMX) / rect.width * 100;
      const dy = (touch.clientY - d.startMY) / rect.height * 100;
      const newPos = calcPos(d, dx, dy);
      if (d.boxKey) {
        const key = d.boxKey;
        setPendingPos(prev => { const n = new Map(prev); n.set(key, newPos); return n; });
        setPendingDirty(true);
      } else {
        if (d.target === 'gift') setEditGiftPos(newPos);
        else setEditPos(newPos);
        setIsDirty(true);
      }
    };
    const onMouseUp = async () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      // Auto-save only for mini-canvas (non-boxKey) drags
      if (!d.boxKey && selectedAnimRef.current && isDirtyRef.current && editPosRef.current) {
        await savePositionRef.current(editPosRef.current);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onMouseUp);
    document.addEventListener('touchcancel', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onMouseUp);
      document.removeEventListener('touchcancel', onMouseUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fermer le menu de type si clic en dehors
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node)) {
        setShowTypeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const savePosition = useCallback(async (pos: { x: number; y: number; w: number; h: number }) => {
    const anim = selectedAnimRef.current; if (!anim) return;
    setSaving(true);
    try {
      const gp = editGiftPosRef.current;
      await updateThemeAnimation(anim.id, { name: anim.name, animation_file: anim.animation_file, animation_type: (anim.animation_type ?? 'video') as ThemeAnimation['animation_type'], trigger_type: anim.trigger_type ?? 'auto', text_content: anim.text_content, text_color: anim.text_color ?? '#ffffff', text_size: anim.text_size ?? 48, text_effect: anim.text_effect ?? 'fade', text_duration: anim.text_duration ?? 5000, text_font: anim.text_font ?? 'sans-serif', text_loops: anim.text_loops ?? 1, text_bg_color: anim.text_bg_color ?? '#000000', text_bg_opacity: anim.text_bg_opacity ?? 0, text_bg_anim: anim.text_bg_anim ?? 'none', show_gift_image: anim.show_gift_image ?? 0, gift_img_x: gp.x, gift_img_y: gp.y, gift_img_w: gp.w, gift_img_h: gp.h, pos_x: pos.x, pos_y: pos.y, pos_w: pos.w, pos_h: pos.h });
      setIsDirty(false);
      if (selected) { const res = await getThemeAnimations(selected.id); setAnimations(res.data); const fresh = res.data.find(a => a.id === anim.id); if (fresh) setSelectedAnim(fresh); }
    } finally { setSaving(false); }
  }, [selected]);
  // Garder savePositionRef à jour pour éviter la closure périmée dans le useEffect mousemove
  useEffect(() => { savePositionRef.current = savePosition; }, [savePosition]);

  // Payload minimal réutilisable pour updateThemeAnimation
  const animPayload = (a: ThemeAnimation, p: { x: number; y: number; w: number; h: number }) => ({
    name: a.name, animation_file: a.animation_file,
    animation_type: (a.animation_type ?? 'video') as ThemeAnimation['animation_type'],
    trigger_type: a.trigger_type ?? 'auto',
    text_content: a.text_content, text_color: a.text_color ?? '#ffffff',
    text_size: a.text_size ?? 48, text_effect: a.text_effect ?? 'fade',
    text_duration: a.text_duration ?? 5000, text_font: a.text_font ?? 'sans-serif',
    text_loops: a.text_loops ?? 1, text_bg_color: a.text_bg_color ?? '#000000',
    text_bg_opacity: a.text_bg_opacity ?? 0, text_bg_anim: a.text_bg_anim ?? 'none',
    show_gift_image: a.show_gift_image ?? 0,
    gift_img_x: a.gift_img_x ?? 5, gift_img_y: a.gift_img_y ?? 5,
    gift_img_w: a.gift_img_w ?? 20, gift_img_h: a.gift_img_h ?? 20,
    gift_label_pos: a.gift_label_pos ?? 'none', gift_label_color: a.gift_label_color ?? '#ffffff',
    gift_label_size: a.gift_label_size ?? 12, gift_cols: a.gift_cols ?? 0,
    queue_max_visible: a.queue_max_visible ?? 5,
    queue_show_avatar: a.queue_show_avatar ?? 1,
    queue_show_counter: a.queue_show_counter ?? 1,
    queue_appear_anim: a.queue_appear_anim ?? 'zoom',
    queue_disappear_anim: a.queue_disappear_anim ?? 'fade',
    queue_auto_hide: a.queue_auto_hide ?? 0,
    queue_particle: a.queue_particle ?? 'none',
    queue_sound: a.queue_sound ?? null,
    queue_min_coins: a.queue_min_coins ?? 0,
    queue_bg_image: a.queue_bg_image ?? null,
    queue_q_bg_image: a.queue_q_bg_image ?? null,
    queue_q_bg_color: a.queue_q_bg_color ?? '#111111',
    queue_q_pos_x: a.queue_q_pos_x ?? 15, queue_q_pos_y: a.queue_q_pos_y ?? 38,
    queue_q_pos_w: a.queue_q_pos_w ?? 70, queue_q_pos_h: a.queue_q_pos_h ?? 24,
    queue_ctrl_pos_x: a.queue_ctrl_pos_x ?? 2, queue_ctrl_pos_y: a.queue_ctrl_pos_y ?? 89,
    queue_ctrl_pos_w: a.queue_ctrl_pos_w ?? 34, queue_ctrl_pos_h: a.queue_ctrl_pos_h ?? 8,
    queue_show_gift: a.queue_show_gift ?? 0,
    queue_gift_pos_x: a.queue_gift_pos_x ?? 70, queue_gift_pos_y: a.queue_gift_pos_y ?? 5,
    queue_gift_pos_w: a.queue_gift_pos_w ?? 18, queue_gift_pos_h: a.queue_gift_pos_h ?? 10,
    queue_gift_label: a.queue_gift_label ?? null, queue_gift_label_pos: a.queue_gift_label_pos ?? 'none',
    queue_gift_label_color: a.queue_gift_label_color ?? '#ffffff', queue_gift_label_size: a.queue_gift_label_size ?? 14,
    queue_show_pgift: a.queue_show_pgift ?? 0,
    queue_pgift_pos_x: a.queue_pgift_pos_x ?? 70, queue_pgift_pos_y: a.queue_pgift_pos_y ?? 18,
    queue_pgift_pos_w: a.queue_pgift_pos_w ?? 18, queue_pgift_pos_h: a.queue_pgift_pos_h ?? 10,
    queue_pgift_label: a.queue_pgift_label ?? null, queue_pgift_label_pos: a.queue_pgift_label_pos ?? 'none',
    queue_pgift_label_color: a.queue_pgift_label_color ?? '#ffffff', queue_pgift_label_size: a.queue_pgift_label_size ?? 14,
    queue_tts_enabled: ((a as any).queue_tts_enabled ? 1 : 0),
    queue_tts_voice_id: (a as any).queue_tts_voice_id ?? null,
    pos_x: p.x, pos_y: p.y, pos_w: p.w, pos_h: p.h,
  });

  // ── Fullscreen 3-panel utilities ──────────────────────────────────────────

  // Initialise pendingPos depuis les animations courantes (appelé à l'ouverture plein écran)
  const initPendingPos = useCallback((anims: ThemeAnimation[]) => {
    const m = new Map<string, { x: number; y: number; w: number; h: number }>();
    anims.forEach(a => {
      m.set(`anim-${a.id}`, { x: a.pos_x, y: a.pos_y, w: a.pos_w, h: a.pos_h });
      if (a.show_gift_image) {
        m.set(`gift-${a.id}`, { x: a.gift_img_x ?? 5, y: a.gift_img_y ?? 5, w: a.gift_img_w ?? 20, h: a.gift_img_h ?? 20 });
      }
      if (a.animation_type === 'queue' || a.animation_type === 'raffle') {
        m.set(`qq-${a.id}`, { x: a.queue_q_pos_x ?? 15, y: a.queue_q_pos_y ?? 38, w: a.queue_q_pos_w ?? 70, h: a.queue_q_pos_h ?? 24 });
        if (a.animation_type === 'queue' || a.queue_sound) m.set(`qc-${a.id}`, { x: a.queue_ctrl_pos_x ?? 2, y: a.queue_ctrl_pos_y ?? 89, w: a.queue_ctrl_pos_w ?? 34, h: a.queue_ctrl_pos_h ?? 8 });
        if (a.queue_show_gift) m.set(`qg-${a.id}`, { x: a.queue_gift_pos_x ?? 70, y: a.queue_gift_pos_y ?? 5, w: a.queue_gift_pos_w ?? 18, h: a.queue_gift_pos_h ?? 10 });
        if (a.animation_type === 'raffle' || a.queue_show_pgift) m.set(`qpg-${a.id}`, { x: a.queue_pgift_pos_x ?? 55, y: a.queue_pgift_pos_y ?? 42, w: a.queue_pgift_pos_w ?? 36, h: a.queue_pgift_pos_h ?? 22 });
      }
    });
    setPendingPos(m);
    setPendingDirty(false);
  }, []);

  // Sauvegarde unique de toutes les positions accumulées
  const saveAllPending = useCallback(async () => {
    if (!selected) return;
    setSavingPending(true);
    try {
      const saves: Promise<unknown>[] = [];
      Array.from(pendingPos.entries()).forEach(([key, pos]) => {
        if (!key.startsWith('anim-')) return;
        const id = parseInt(key.replace('anim-', ''));
        const anim = animations.find(a => a.id === id);
        if (!anim) return;
        const giftPos = pendingPos.get(`gift-${id}`);
        const qqPos = pendingPos.get(`qq-${id}`);
        const qcPos = pendingPos.get(`qc-${id}`);
        const qgPos = pendingPos.get(`qg-${id}`);
        const qpgPos = pendingPos.get(`qpg-${id}`);
        saves.push(updateThemeAnimation(id, {
          ...animPayload(anim, pos),
          ...(giftPos ? { gift_img_x: giftPos.x, gift_img_y: giftPos.y, gift_img_w: giftPos.w, gift_img_h: giftPos.h } : {}),
          ...(qqPos ? { queue_q_pos_x: qqPos.x, queue_q_pos_y: qqPos.y, queue_q_pos_w: qqPos.w, queue_q_pos_h: qqPos.h } : {}),
          ...(qcPos ? { queue_ctrl_pos_x: qcPos.x, queue_ctrl_pos_y: qcPos.y, queue_ctrl_pos_w: qcPos.w, queue_ctrl_pos_h: qcPos.h } : {}),
          ...(qgPos ? { queue_gift_pos_x: qgPos.x, queue_gift_pos_y: qgPos.y, queue_gift_pos_w: qgPos.w, queue_gift_pos_h: qgPos.h } : {}),
          ...(qpgPos ? { queue_pgift_pos_x: qpgPos.x, queue_pgift_pos_y: qpgPos.y, queue_pgift_pos_w: qpgPos.w, queue_pgift_pos_h: qpgPos.h } : {}),
        }));
      });
      await Promise.all(saves);
      const res = await getThemeAnimations(selected.id);
      setAnimations(res.data);
      initPendingPos(res.data);
      setPendingDirty(false);
    } finally { setSavingPending(false); }
  }, [selected, pendingPos, animations, initPendingPos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aligne un ensemble de boîtes (par clé 'anim-{id}' / 'gift-{id}') sans appel API
  const bulkAlignPending = useCallback((
    computePos: (pos: { x: number; y: number; w: number; h: number }) => { x: number; y: number; w: number; h: number },
    keys: string[]
  ) => {
    if (keys.length === 0) return;
    setPendingPos(m => {
      const n = new Map(m);
      keys.forEach(key => { const pos = n.get(key); if (pos) n.set(key, computePos(pos)); });
      return n;
    });
    setPendingDirty(true);
  }, []);

  // Dispose en ligne ou en colonne un ensemble de boîtes sans appel API
  const bulkArrangePending = useCallback((mode: 'row' | 'col', keys: string[], gap = 1) => {
    if (keys.length === 0) return;
    setPendingPos(m => {
      const n = new Map(m);
      const items = keys.map(k => ({ key: k, pos: n.get(k)! })).filter(i => i.pos);
      if (items.length === 0) return n;
      items.sort((a, b) => mode === 'row' ? a.pos.x - b.pos.x : a.pos.y - b.pos.y);
      let cursor = mode === 'row' ? items[0].pos.x : items[0].pos.y;
      const refX = items[0].pos.x, refY = items[0].pos.y;
      items.forEach(({ key, pos }) => {
        const np = mode === 'row'
          ? { x: cursor, y: refY, w: pos.w, h: pos.h }
          : { x: refX, y: cursor, w: pos.w, h: pos.h };
        n.set(key, np);
        cursor += (mode === 'row' ? pos.w : pos.h) + gap;
      });
      return n;
    });
    setPendingDirty(true);
  }, []);

  // Démarre un drag fullscreen (met à jour pendingPos via le handler document-level)
  const startDragFullscreen = (
    e: React.MouseEvent, mode: DragState['mode'],
    boxKey: string, pos: { x: number; y: number; w: number; h: number }
  ) => {
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { mode, target: 'anim', boxKey, startMX: e.clientX, startMY: e.clientY, startPos: { ...pos } };
  };
  const startTouchFullscreen = (
    e: React.TouchEvent, mode: DragState['mode'],
    boxKey: string, pos: { x: number; y: number; w: number; h: number }
  ) => {
    const touch = e.touches[0];
    if (!touch) return;
    e.preventDefault(); e.stopPropagation();
    dragRef.current = { mode, target: 'anim', boxKey, startMX: touch.clientX, startMY: touch.clientY, startPos: { ...pos } };
  };

  const openAddTheme = () => { setEditingTheme(null); setThemeForm(emptyTheme()); setImageFile(null); setImagePreview(''); setShowThemeForm(true); };
  const openEditTheme = (t: Theme, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTheme(t); setThemeForm({ name: t.name, slug: t.slug, image_url: t.image_url ?? '' });
    setImageFile(null); setImagePreview(t.image_url ?? ''); setShowThemeForm(true);
  };
  const saveTheme = async () => {
    if (!themeForm.name.trim()) return;
    const slug = themeForm.slug || slugify(themeForm.name);
    let image_url = themeForm.image_url || null;
    if (editingTheme) { await updateTheme(editingTheme.id, themeForm.name, image_url); if (imageFile) { const r = await uploadThemeImage(editingTheme.id, imageFile); image_url = r.data.image_url; } }
    else { const r = await createTheme(themeForm.name, slug, image_url); if (imageFile) await uploadThemeImage(r.data.id, imageFile); }
    setShowThemeForm(false);
    const res = await getThemes(); setThemes(res.data);
    if (editingTheme) { const fresh = res.data.find(t => t.id === editingTheme.id); if (fresh) setSelected(fresh); }
  };
  const handleDeleteTheme = async (t: Theme, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer le thème "${t.name}" ?`)) return;
    await deleteTheme(t.id);
    if (selected?.id === t.id) { setSelected(null); setSelectedAnim(null); }
    const res = await getThemes(); setThemes(res.data);
  };

  const openAddAnimWithType = (type: AnimFormState['animation_type']) => {
    setShowTypeMenu(false);
    setEditingAnim(null);
    setAnimError('');
    const isDynamic = type === 'text_dynamic';
    const isGiftGrid = type === 'gift_grid';
    const isRaffle = type === 'raffle';
    const isMobileComments = type === 'mobile_comments';
    const isBanner = type === 'banner';
    setAnimForm({
      ...emptyAnimForm(),
      animation_type: type,
      trigger_type: isDynamic ? 'manual' : 'auto',
      text_effect: isRaffle ? 'gift_random' : isBanner ? 'scroll-left' : emptyAnimForm().text_effect,
      text_color: isMobileComments ? '#fbbf24' : emptyAnimForm().text_color,
      text_size: isMobileComments ? 28 : isBanner ? 42 : emptyAnimForm().text_size,
      text_bg_opacity: isMobileComments ? 35 : emptyAnimForm().text_bg_opacity,
      text_content: isBanner ? defaultBannerJson() : emptyAnimForm().text_content,
      queue_max_visible: isRaffle || isMobileComments ? 5 : emptyAnimForm().queue_max_visible,
      queue_q_pos_x: isRaffle ? 10 : emptyAnimForm().queue_q_pos_x,
      queue_q_pos_y: isRaffle ? 38 : emptyAnimForm().queue_q_pos_y,
      queue_q_pos_w: isRaffle ? 80 : emptyAnimForm().queue_q_pos_w,
      queue_q_pos_h: isRaffle ? 22 : emptyAnimForm().queue_q_pos_h,
      queue_ctrl_pos_x: isRaffle ? 25 : emptyAnimForm().queue_ctrl_pos_x,
      queue_ctrl_pos_y: isRaffle ? 28 : emptyAnimForm().queue_ctrl_pos_y,
      queue_ctrl_pos_w: isRaffle ? 50 : emptyAnimForm().queue_ctrl_pos_w,
      queue_ctrl_pos_h: isRaffle ? 18 : emptyAnimForm().queue_ctrl_pos_h,
      queue_pgift_pos_x: isRaffle ? 18 : emptyAnimForm().queue_pgift_pos_x,
      queue_pgift_pos_y: isRaffle ? 32 : emptyAnimForm().queue_pgift_pos_y,
      queue_pgift_pos_w: isRaffle ? 64 : emptyAnimForm().queue_pgift_pos_w,
      queue_pgift_pos_h: isRaffle ? 36 : emptyAnimForm().queue_pgift_pos_h,
      name: isDynamic ? 'Texte dynamique' : isGiftGrid ? 'Zone Cadeaux' : isRaffle ? 'Tirage au sort' : isMobileComments ? 'Commentaires mobile' : isBanner ? 'Bannière' : '',
    });
    if (isBanner) {
      setEditPos({ x: 0, y: 82, w: 100, h: 10 });
    } else {
      setEditPos(null);
    }
    setShowAnimForm(true);
  };
  const openAddAnim = () => { setShowTypeMenu(v => !v); };
  const openEditAnim = (a: ThemeAnimation, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAnim(a);
    setAnimError('');
    setAnimForm({
      name: a.name,
      animation_file: a.animation_file ?? '',
      animation_type: (a.animation_type ?? 'video') as AnimFormState['animation_type'],
      trigger_type: a.trigger_type ?? 'auto',
      text_content: a.text_content ?? '',
      text_color: a.text_color ?? '#ffffff',
      text_size: a.text_size ?? 48,
      text_effect: a.text_effect ?? 'fade',
      text_duration: (a.text_duration ?? 5000) / 1000,
      text_font: a.text_font ?? 'sans-serif',
      text_loops: a.text_loops ?? 1,
      text_bg_color: a.text_bg_color ?? '#000000',
      text_bg_opacity: a.text_bg_opacity ?? 0,
      text_bg_anim: a.text_bg_anim ?? 'none',
      show_gift_image: !!(a.show_gift_image),
      gift_img_x: a.gift_img_x ?? 5,
      gift_img_y: a.gift_img_y ?? 5,
      gift_img_w: a.gift_img_w ?? 20,
      gift_img_h: a.gift_img_h ?? 20,
      gift_label_pos: (a.gift_label_pos ?? 'none') as AnimFormState['gift_label_pos'],
      gift_label_color: a.gift_label_color ?? '#ffffff',
      gift_label_size: a.gift_label_size ?? 12,
      gift_cols: a.gift_cols ?? 0,
      queue_max_visible: a.queue_max_visible ?? 5,
      queue_show_avatar: a.queue_show_avatar !== 0 && a.queue_show_avatar !== false,
      queue_show_counter: a.queue_show_counter !== 0 && a.queue_show_counter !== false,
      queue_appear_anim: a.queue_appear_anim ?? 'zoom',
      queue_disappear_anim: a.queue_disappear_anim ?? 'fade',
      queue_auto_hide: a.queue_auto_hide ?? 0,
      queue_particle: a.queue_particle ?? 'none',
      queue_sound: a.queue_sound ?? '',
      queue_min_coins: a.queue_min_coins ?? 0,
      queue_bg_image: a.queue_bg_image ?? '',
      queue_q_bg_image: a.queue_q_bg_image ?? '',
      queue_q_bg_color: a.queue_q_bg_color ?? '#111111',
      queue_q_pos_x: a.queue_q_pos_x ?? 15,
      queue_q_pos_y: a.queue_q_pos_y ?? 38,
      queue_q_pos_w: a.queue_q_pos_w ?? 70,
      queue_q_pos_h: a.queue_q_pos_h ?? 24,
      queue_ctrl_pos_x: a.queue_ctrl_pos_x ?? 2,
      queue_ctrl_pos_y: a.queue_ctrl_pos_y ?? 89,
      queue_ctrl_pos_w: a.queue_ctrl_pos_w ?? 34,
      queue_ctrl_pos_h: a.queue_ctrl_pos_h ?? 8,
      queue_show_gift: a.queue_show_gift !== 0 && a.queue_show_gift !== false && a.queue_show_gift !== undefined,
      queue_gift_pos_x: a.queue_gift_pos_x ?? 70,
      queue_gift_pos_y: a.queue_gift_pos_y ?? 5,
      queue_gift_pos_w: a.queue_gift_pos_w ?? 18,
      queue_gift_pos_h: a.queue_gift_pos_h ?? 10,
      queue_gift_label: a.queue_gift_label ?? '',
      queue_gift_label_pos: a.queue_gift_label_pos ?? 'none',
      queue_gift_label_color: a.queue_gift_label_color ?? '#ffffff',
      queue_gift_label_size: a.queue_gift_label_size ?? 14,
      queue_show_pgift: a.queue_show_pgift !== 0 && a.queue_show_pgift !== false && a.queue_show_pgift !== undefined,
      queue_pgift_pos_x: a.queue_pgift_pos_x ?? 70,
      queue_pgift_pos_y: a.queue_pgift_pos_y ?? 18,
      queue_pgift_pos_w: a.queue_pgift_pos_w ?? 18,
      queue_pgift_pos_h: a.queue_pgift_pos_h ?? 10,
      queue_pgift_label: a.queue_pgift_label ?? '',
      queue_pgift_label_pos: a.queue_pgift_label_pos ?? 'none',
      queue_pgift_label_color: a.queue_pgift_label_color ?? '#ffffff',
      queue_pgift_label_size: a.queue_pgift_label_size ?? 14,
      queue_tts_enabled: !!(a as any).queue_tts_enabled,
      queue_tts_voice_id: (a as any).queue_tts_voice_id ?? '',
      mobile_comment_voice_id: (a as any).mobile_comment_voice_id ?? '',
      mobile_comment_voice_name: (a as any).mobile_comment_voice_name ?? '',
    });
    setEditGiftPos({ x: a.gift_img_x ?? 5, y: a.gift_img_y ?? 5, w: a.gift_img_w ?? 20, h: a.gift_img_h ?? 20 });
    setShowAnimForm(true);
  };

  // CRUD éléments statiques
  const saveStaticElem = async () => {
    if (!selected || !staticForm.image_file || !staticForm.name.trim()) return;
    if (editingStaticId !== null) {
      await updateStaticElement(editingStaticId, { ...staticForm });
    } else {
      await createStaticElement(selected.id, { ...staticForm });
    }
    setShowStaticForm(false); setEditingStaticId(null); setStaticForm(emptyStaticForm());
    await loadStaticElems(selected.id);
  };
  const deleteStaticElem = async (id: number) => {
    if (!window.confirm('Supprimer cet élément permanent ?')) return;
    await deleteStaticElement(id);
    if (selected) await loadStaticElems(selected.id);
  };
  const openEditStatic = (e: StaticElement) => {
    setEditingStaticId(e.id);
    setStaticForm({ name: e.name, image_file: e.image_file, pos_x: e.pos_x, pos_y: e.pos_y, pos_w: e.pos_w, pos_h: e.pos_h, layer: e.layer, sort_order: e.sort_order });
    setShowStaticForm(true);
  };
  const saveAnim = async () => {
    if (!selected) return;
    const isGiftGrid = animForm.animation_type === 'gift_grid';
    const isMedia = animForm.animation_type === 'video' || animForm.animation_type === 'audio';
    const isRaffle = animForm.animation_type === 'raffle';
    const isMobileComments = animForm.animation_type === 'mobile_comments';
    const isBanner = animForm.animation_type === 'banner';
    const isText = !isMedia && !isGiftGrid && !isRaffle && !isMobileComments;
    const isDynamic = animForm.animation_type === 'text_dynamic';
    // Auto-name
    const finalName = isDynamic ? 'Texte dynamique' : isGiftGrid ? (animForm.name.trim() || 'Zone Cadeaux') : isRaffle ? (animForm.name.trim() || 'Tirage au sort') : isMobileComments ? (animForm.name.trim() || 'Commentaires mobile') : isBanner ? (animForm.name.trim() || 'Bannière') : animForm.name.trim();
    if (!finalName) { setAnimError('Le nom est requis.'); return; }
    if (isMedia && !animForm.animation_file) { setAnimError('Choisissez un fichier.'); return; }
    if (isBanner) {
      try {
        const parsed = JSON.parse(animForm.text_content || '{}');
        if (!Array.isArray(parsed.patterns) || parsed.patterns.length === 0) throw new Error('patterns');
      } catch {
        setAnimError('La configuration JSON de la bannière est invalide.');
        return;
      }
    }
    setAnimError('');
    const payload = {
      name: finalName,
      animation_file: isRaffle ? (animForm.animation_file || null) : (isText || isGiftGrid || isMobileComments) ? null : animForm.animation_file,
      animation_type: animForm.animation_type,
      trigger_type: animForm.trigger_type,
      text_content: isText ? animForm.text_content : null,
      text_color: animForm.text_color,
      text_size: animForm.text_size,
      text_effect: animForm.text_effect,
      text_duration: Math.round(animForm.text_duration * 1000),
      text_font: animForm.text_font,
      text_loops: animForm.text_loops,
      text_bg_color: animForm.text_bg_color,
      text_bg_opacity: animForm.text_bg_opacity,
      text_bg_anim: animForm.text_bg_anim,
      show_gift_image: animForm.trigger_type === 'auto' && animForm.show_gift_image ? 1 : 0,
      gift_img_x: animForm.gift_img_x,
      gift_img_y: animForm.gift_img_y,
      gift_img_w: animForm.gift_img_w,
      gift_img_h: animForm.gift_img_h,
      gift_label_pos: animForm.gift_label_pos,
      gift_label_color: animForm.gift_label_color,
      gift_label_size: animForm.gift_label_size,
      gift_cols: animForm.gift_cols,
      queue_max_visible: animForm.queue_max_visible,
      queue_show_avatar: animForm.queue_show_avatar ? 1 : 0,
      queue_show_counter: animForm.queue_show_counter ? 1 : 0,
      queue_appear_anim: animForm.queue_appear_anim,
      queue_disappear_anim: animForm.queue_disappear_anim,
      queue_auto_hide: animForm.queue_auto_hide,
      queue_particle: animForm.queue_particle,
      queue_sound: animForm.queue_sound || null,
      queue_min_coins: animForm.queue_min_coins,
      queue_bg_image: animForm.queue_bg_image || null,
      queue_q_bg_image: animForm.queue_q_bg_image || null,
      queue_q_bg_color: animForm.queue_q_bg_color,
      queue_q_pos_x: animForm.queue_q_pos_x,
      queue_q_pos_y: animForm.queue_q_pos_y,
      queue_q_pos_w: animForm.queue_q_pos_w,
      queue_q_pos_h: animForm.queue_q_pos_h,
      queue_ctrl_pos_x: animForm.queue_ctrl_pos_x,
      queue_ctrl_pos_y: animForm.queue_ctrl_pos_y,
      queue_ctrl_pos_w: animForm.queue_ctrl_pos_w,
      queue_ctrl_pos_h: animForm.queue_ctrl_pos_h,
      queue_show_gift: animForm.queue_show_gift ? 1 : 0,
      queue_gift_pos_x: animForm.queue_gift_pos_x,
      queue_gift_pos_y: animForm.queue_gift_pos_y,
      queue_gift_pos_w: animForm.queue_gift_pos_w,
      queue_gift_pos_h: animForm.queue_gift_pos_h,
      queue_gift_label: animForm.queue_gift_label || null,
      queue_gift_label_pos: animForm.queue_gift_label_pos,
      queue_gift_label_color: animForm.queue_gift_label_color,
      queue_gift_label_size: animForm.queue_gift_label_size,
      queue_show_pgift: animForm.queue_show_pgift ? 1 : 0,
      queue_pgift_pos_x: animForm.queue_pgift_pos_x,
      queue_pgift_pos_y: animForm.queue_pgift_pos_y,
      queue_pgift_pos_w: animForm.queue_pgift_pos_w,
      queue_pgift_pos_h: animForm.queue_pgift_pos_h,
      queue_pgift_label: animForm.queue_pgift_label || null,
      queue_pgift_label_pos: animForm.queue_pgift_label_pos,
      queue_pgift_label_color: animForm.queue_pgift_label_color,
      queue_pgift_label_size: animForm.queue_pgift_label_size,
      queue_tts_enabled: animForm.queue_tts_enabled ? 1 : 0,
      queue_tts_voice_id: animForm.queue_tts_voice_id || null,
      mobile_comment_voice_id: animForm.mobile_comment_voice_id || null,
      mobile_comment_voice_name: animForm.mobile_comment_voice_name || null,
    };
    const gp = editGiftPos;
    try {
      if (editingAnim) {
        await updateThemeAnimation(editingAnim.id, { ...payload, pos_x: editingAnim.pos_x, pos_y: editingAnim.pos_y, pos_w: editingAnim.pos_w, pos_h: editingAnim.pos_h, gift_img_x: gp.x, gift_img_y: gp.y, gift_img_w: gp.w, gift_img_h: gp.h });
      } else {
        await createThemeAnimation(selected.id, { ...payload, pos_x: isRaffle ? 4 : isMobileComments ? 3 : isBanner ? 0 : 0, pos_y: isRaffle ? 55 : isMobileComments ? 68 : isBanner ? 82 : 0, pos_w: isRaffle ? 42 : isMobileComments ? 58 : isBanner ? 100 : 50, pos_h: isRaffle ? 34 : isMobileComments ? 23 : isBanner ? 10 : 30 });
      }
      setShowAnimForm(false);
      await loadAnimations(selected.id);
      const res = await getThemes(); setThemes(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Erreur lors de la sauvegarde.';
      setAnimError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };
  const handleDeleteAnim = async (a: ThemeAnimation, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer l'animation "${a.name}" ?`)) return;
    await deleteThemeAnimation(a.id);
    if (selectedAnim?.id === a.id) { setSelectedAnim(null); setEditPos(null); }
    if (selected) await loadAnimations(selected.id);
    const res = await getThemes(); setThemes(res.data);
  };

  const handleUploadAnimFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !selected) return;
    const validExts = ['.mp4', '.webm', '.mov', '.mp3', '.ogg', '.wav', '.aac'];
    if (!validExts.some(ext => file.name.toLowerCase().endsWith(ext))) return;
    setUploadingAnim(true);
    try {
      const res = await uploadThemeAnimationFile(selected.id, file);
      const data = res.data;
      // Recharger la liste filtrée par thème
      await loadAnimFiles(selected.id);
      // Sélectionner automatiquement le fichier uploadé
      setAnimForm(f => ({ ...f, animation_file: data.filename }));
      if (data.converted && data.message) {
        alert(`✅ ${data.message}`);
      }
    } catch {}
    setUploadingAnim(false); e.target.value = '';
  };

  // Upload générique (image / audio) pour la File d'attente → renvoie le nom du fichier
  const uploadQueueAsset = async (file: File): Promise<string | undefined> => {
    if (!selected) return undefined;
    try {
      const res = await uploadThemeAnimationFile(selected.id, file);
      await loadAnimFiles(selected.id);
      return res.data.filename;
    } catch {
      return undefined;
    }
  };

  const handleOverlayUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file || !selected) return;
    if (!file.name.endsWith('.png')) { setOverlayMsg('Seuls les PNG sont acceptés'); return; }
    setOverlayUploading(true); setOverlayMsg('');
    try {
      await uploadThemeOverlay(selected.id, file); setOverlayMsg('Overlay appliqué !');
      setOverlayCacheBust(Date.now());
      const res = await getThemes(); setThemes(res.data);
      const fresh = res.data.find(t => t.id === selected.id); if (fresh) setSelected(fresh);
      setTimeout(() => setOverlayMsg(''), 3000);
    } catch { setOverlayMsg('Erreur upload'); }
    setOverlayUploading(false); e.target.value = '';
  };
  const handleRemoveOverlay = async () => {
    if (!selected) return;
    await removeThemeOverlay(selected.id); setOverlayMsg('Overlay supprimé');
    setOverlayCacheBust(Date.now());
    const res = await getThemes(); setThemes(res.data);
    const fresh = res.data.find(t => t.id === selected.id); if (fresh) setSelected(fresh);
    setTimeout(() => setOverlayMsg(''), 3000);
  };

  const toggleStreamer = async (streamerId: number, hasTheme: boolean) => {
    if (!selected) return;
    if (hasTheme) await unassignTheme(streamerId, selected.id);
    else await assignTheme(streamerId, selected.id);
    loadThemeStreamers(selected.id);
  };

  const currentOverlayUrl = selected?.overlay_file
    ? overlayUrl(selected.overlay_file) + (overlayCacheBust ? `?t=${overlayCacheBust}` : '')
    : null;
  const filteredThemes = themes.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
  // Base = themeStreamers (renvoyé par le serveur, déjà limité à l'équipe pour un
  // responsable ; tous les streamers pour un admin).
  const visibleThemeStreamers = themeStreamers
    .filter(s =>
      !streamerSearch ||
      s.display_name.toLowerCase().includes(streamerSearch.toLowerCase()) ||
      (s.username || '').toLowerCase().includes(streamerSearch.toLowerCase())
    )
    .sort((a, b) => {
      const aHas = a.has_theme === 1, bHas = b.has_theme === 1;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return a.display_name.localeCompare(b.display_name, 'fr', { sensitivity: 'base' });
    });
  const mobileOverlayControls = selected && isAdmin ? (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
      <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Overlay PNG</div>
      {selected.overlay_file ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ color: '#16a34a', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>✓</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.overlay_file}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <label style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#64748b', fontSize: 11, cursor: 'pointer', background: '#f8fafc' }}>
              Changer
              <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
            </label>
            <button onClick={handleRemoveOverlay} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>
              Supprimer
            </button>
          </div>
        </div>
      ) : (
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: 12, cursor: 'pointer', background: '#f8fafc' }}>
          ↑ {overlayUploading ? 'Upload…' : 'Choisir un overlay PNG'}
          <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
        </label>
      )}
      {overlayMsg && <div style={{ marginTop: 6, fontSize: 11, color: overlayMsg.includes('!') || overlayMsg.includes('supprimé') ? '#16a34a' : '#dc2626' }}>{overlayMsg}</div>}
    </div>
  ) : null;

  // ── Mobile page navigation ───────────────────────────────
  if (isMobile) {
    // Si thème sélectionné → page dédiée
    if (selected) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          {/* Header back */}
          <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button onClick={() => { setSelected(null); setSelectedAnim(null); setEditPos(null); setExpandedId(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: '4px 8px', borderRadius: 6 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Thèmes
            </button>
            <div style={{ width: 1, height: 16, background: '#e2e8f0' }} />
            {selected.image_url && (
              <img src={selected.image_url} alt={selected.name} style={{ width: 28, height: 42, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
            <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 15, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</div>
            {isAdmin && (
              <div style={{ position: 'relative' }} ref={typeMenuRef}>
                <button onClick={openAddAnim} style={btnPrimary}>+ Animation ▾</button>
                {showTypeMenu && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 190, zIndex: 60, overflow: 'hidden' }}>
                    <button onClick={() => openAddAnimWithType('video')} style={dropdownItem}>🎬 Vidéo</button>
                    <button onClick={() => openAddAnimWithType('audio')} style={dropdownItem}>🔊 Son (audio)</button>
                    <button onClick={() => openAddAnimWithType('text')} style={dropdownItem}>📝 Texte (statique)</button>
                    <button onClick={() => openAddAnimWithType('text_dynamic')} style={dropdownItem}>⌨️ Texte dynamique</button>
                    <button onClick={() => openAddAnimWithType('banner')} style={dropdownItem}>🪧 Bannière</button>
                    <button onClick={() => openAddAnimWithType('gift_grid')} style={dropdownItem}>🎁 Cadeaux Games</button>
                    <button onClick={() => openAddAnimWithType('queue')} style={dropdownItem}>📋 File d'attente</button>
                    <button onClick={() => openAddAnimWithType('raffle')} style={dropdownItem}>Tirage au sort</button>
                    <button onClick={() => openAddAnimWithType('mobile_comments')} style={{ ...dropdownItem, borderBottom: 'none' }}>Commentaires mobile</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Detail content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#f8fafc' }}>
            <select
              value={detailTab === 'streamers' ? 'streamers' : 'animations'}
              onChange={e => setDetailTab(e.target.value as 'animations' | 'streamers')}
              style={{ ...inputLight, width: '100%', marginBottom: 12, background: '#fff' }}
            >
              <option value="animations">Animations ({animations.length})</option>
              <option value="streamers">Streamers</option>
            </select>
            {detailTab !== 'streamers' && (
            <>
            <div style={{ display: 'flex', gap: 12 }}>
              {/* Mini preview */}
              <div style={{ width: 100, flexShrink: 0 }}>
                <button
                  onClick={() => { initPendingPos(animations); setCheckedAnimIds(new Set()); setCheckedGiftIds(new Set()); setRefKey(null); setFullscreenMobileTab('theme'); setIsFullscreen(true); }}
                  style={{ marginBottom: 6, width: '100%', padding: '5px', fontSize: 11, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer' }}
                >
                  Plein écran
                </button>
                <div style={{ position: 'relative', width: 100, height: 178, background: '#0a0a0a', borderRadius: 8, overflow: 'hidden' }}>
                  {currentOverlayUrl && (
                    <img src={currentOverlayUrl} alt="overlay" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: 1 }} />
                  )}
                  {selectedAnim && editPos && (
                    <div style={{ position: 'absolute', left: `${editPos.x}%`, top: `${editPos.y}%`, width: `${editPos.w}%`, height: `${editPos.h}%`, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {(selectedAnim.animation_type === 'text' || selectedAnim.animation_type === 'text_dynamic') ? (
                        <div style={{ color: selectedAnim.text_color ?? '#fff', fontSize: Math.min((selectedAnim.text_size ?? 48) * 0.15, 10), fontWeight: 700, textAlign: 'center', textShadow: '1px 1px 3px rgba(0,0,0,0.8)', padding: 2, overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.2 }}>
                          {selectedAnim.animation_type === 'text_dynamic' ? '⌨️' : (selectedAnim.text_content || '📝')}
                        </div>
                      ) : (selectedAnim.animation_type === 'queue' || selectedAnim.animation_type === 'raffle') ? (
                        <div style={{ color: selectedAnim.text_color ?? '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>📋</div>
                      ) : (
                        <video ref={videoRef} key={selectedAnim.animation_file ?? ''} src={animationFileUrl(selectedAnim.animation_file ?? '')}
                          loop autoPlay playsInline muted
                          onError={e => { const v = e.currentTarget; v.style.display='none'; const msg = document.createElement('div'); msg.textContent='⚠️ Codec non supporté (essayez H.264 / .mp4)'; msg.style.cssText='color:#f66;font-size:9px;text-align:center;padding:4px;'; v.parentElement?.appendChild(msg); }}
                          style={{ width: '100%', height: '100%', objectFit: 'fill' }} />
                      )}
                    </div>
                  )}
                  {!selectedAnim && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                      <span style={{ color: '#555', fontSize: 9, textAlign: 'center', lineHeight: 1.6 }}>Sélectionne<br/>une animation →</span>
                    </div>
                  )}
                </div>
                {selectedAnim && selectedAnim.animation_type === 'video' && (
                  <button
                    onClick={() => { const v = videoRef.current; if (!v) return; if (isPlaying) { v.pause(); setIsPlaying(false); } else { v.play(); setIsPlaying(true); } }}
                    style={{ marginTop: 6, width: '100%', padding: '5px', fontSize: 11, borderRadius: 6, border: `1px solid ${isPlaying ? '#bbf7d0' : '#e2e8f0'}`, background: isPlaying ? '#f0fdf4' : '#f8fafc', color: isPlaying ? '#16a34a' : '#64748b', cursor: 'pointer' }}>
                    {isPlaying ? '⏸' : '▶ Aperçu'}
                  </button>
                )}
                {mobileOverlayControls}
              </div>

              {/* Animations list */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {animations.map(a => {
                    const isAct = selectedAnim?.id === a.id;
                    return (
                      <div key={a.id} onClick={() => selectAnim(a)}
                        style={{ background: isAct ? '#eff6ff' : '#fff', border: `1px solid ${isAct ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 10px', cursor: 'pointer' }}>
                        <div style={{ color: isAct ? '#2563eb' : '#0f172a', fontWeight: 600, fontSize: 12 }}>{a.name}</div>
                        <div style={{ color: '#94a3b8', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.animation_type === 'gift_grid' ? '🎁 Zone Cadeaux Games' : a.animation_type === 'queue' ? '📋 ' + (a.name || "File d'attente") : a.animation_type === 'text' ? '📝 ' + (a.text_content || '(texte vide)') : a.animation_type === 'text_dynamic' ? '⌨️ Texte dynamique' : a.animation_type === 'audio' ? '🔊 ' + a.animation_file : a.animation_file}
                        </div>
                        <div style={{ marginTop: 2 }}>
                          <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: a.trigger_type === 'manual' ? '#f5f3ff' : '#f0f9ff', color: a.trigger_type === 'manual' ? '#7c3aed' : '#2563eb' }}>
                            {a.trigger_type === 'manual' ? '🎛️ Manuel' : '🎁 Auto'}
                          </span>
                        </div>
                        {isAdmin && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                            <button onClick={e => openEditAnim(a, e)} style={iconBtn}>✏️</button>
                            <button onClick={e => handleDeleteAnim(a, e)} style={{ ...iconBtn, color: '#dc2626' }}>🗑</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {animations.length === 0 && (
                    <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>Aucune animation</div>
                  )}
                </div>
              </div>
            </div>

            {/* Overlay section (admin) */}
            {false && selected && isAdmin && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Overlay PNG</div>
                {selected!.overlay_file ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ color: '#16a34a', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span>✓</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected!.overlay_file}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <label style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#64748b', fontSize: 11, cursor: 'pointer', background: '#f8fafc' }}>
                        Changer
                        <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
                      </label>
                      <button onClick={handleRemoveOverlay} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>
                        Supprimer
                      </button>
                    </div>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: 12, cursor: 'pointer', background: '#f8fafc' }}>
                    ↑ {overlayUploading ? 'Upload…' : 'Choisir un overlay PNG'}
                    <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
                  </label>
                )}
                {overlayMsg && <div style={{ marginTop: 6, fontSize: 11, color: overlayMsg.includes('!') || overlayMsg.includes('supprimé') ? '#16a34a' : '#dc2626' }}>{overlayMsg}</div>}
              </div>
            )}

            </>
            )}

            {detailTab === 'streamers' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
                  <input
                    value={streamerSearch}
                    onChange={e => setStreamerSearch(e.target.value)}
                    placeholder="Rechercher un streamer…"
                    style={{ ...inputLight, paddingLeft: 32, width: '100%', boxSizing: 'border-box', background: '#fff' }}
                  />
                </div>
                {visibleThemeStreamers.map(s => {
                  const has = s.has_theme === 1;
                  return (
                    <div key={s.id} style={{ background: '#fff', border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: has ? '#ede9fe' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: has ? '#7c3aed' : '#94a3b8', flexShrink: 0, overflow: 'hidden' }}>
                        {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : s.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name}</div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => toggleStreamer(s.id, has)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, background: has ? '#eff6ff' : '#f8fafc', color: has ? '#2563eb' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: has ? 600 : 400 }}>
                          {has ? '✓ Assigné' : '+ Assigner'}
                        </button>
                      )}
                    </div>
                  );
                })}
                {visibleThemeStreamers.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>Aucun streamer trouvé</div>
                )}
              </div>
            )}
          </div>

          {isFullscreen && (() => {
            const checkedKeys = [
              ...Array.from(checkedAnimIds).map(id => `anim-${id}`),
              ...Array.from(checkedGiftIds).map(id => `gift-${id}`),
            ];
            const refPos = refKey ? pendingPos.get(refKey) : null;
            const saveCount = Array.from(pendingPos.keys()).filter(k => k.startsWith('anim-')).length;
            const sectionTitleMobile = (t: string) => (
              <div style={{ color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: '10px 0 6px' }}>{t}</div>
            );
            const toolBtn = (label: string, disabled: boolean, onClick: () => void) => (
              <button disabled={disabled} onClick={onClick}
                style={{ padding: '8px 6px', borderRadius: 7, fontSize: 11, cursor: disabled ? 'not-allowed' : 'pointer', border: '1px solid #1e293b', background: '#111827', color: '#94a3b8', opacity: disabled ? 0.45 : 1 }}>
                {label}
              </button>
            );
            return (
              <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0d1117', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, paddingBottom: 116 }}>
                  {fullscreenMobileTab === 'theme' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                      <button onClick={() => setShowSafeZone(v => !v)}
                        style={{ width: '100%', padding: '9px 10px', borderRadius: 8, border: `1px solid ${showSafeZone ? '#16a34a' : '#1e293b'}`, background: showSafeZone ? '#052e16' : '#111827', color: showSafeZone ? '#86efac' : '#cbd5e1', fontSize: 12, fontWeight: showSafeZone ? 700 : 500 }}>
                        {showSafeZone ? 'Zone sûre affichée' : 'Afficher la zone sûre'}
                      </button>
                      <div ref={previewRefFull} style={{ position: 'relative', width: 'min(78vw, 360px)', aspectRatio: '9 / 16', background: '#0a0a0a', borderRadius: 14, overflow: 'hidden', border: '1px solid #1e293b', touchAction: 'none' }}>
                        {currentOverlayUrl && (
                          <img src={currentOverlayUrl} alt="overlay" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: 1 }} />
                        )}
                        {Array.from(checkedAnimIds).map(id => {
                          const anim = animations.find(a => a.id === id);
                          if (!anim) return null;
                          const pos = pendingPos.get(`anim-${id}`) ?? { x: anim.pos_x, y: anim.pos_y, w: anim.pos_w, h: anim.pos_h };
                          const boxKey = `anim-${id}`;
                          return (
                            <div key={id} style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`, zIndex: 2, border: '1.5px solid #3b82f6', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(59,130,246,0.12)', touchAction: 'none' }}>
                              {anim.animation_type === 'video' && anim.animation_file ? (
                                <video src={animationFileUrl(anim.animation_file)} loop autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'fill' }} />
                              ) : (
                                <span style={{ color: anim.text_color || '#fff', fontSize: 12, fontWeight: 700, textAlign: 'center' }}>{anim.name}</span>
                              )}
                              <div onTouchStart={e => startTouchFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, zIndex: 3 }} />
                              {(['tl','tr','bl','br'] as const).map(corner => (
                                <div key={corner} onTouchStart={e => startTouchFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                  style={{ position: 'absolute', width: 22, height: 22, background: '#3b82f6', border: '2px solid #dbeafe', borderRadius: 6, zIndex: 4,
                                    ...(corner === 'tl' ? { top: -11, left: -11 } : corner === 'tr' ? { top: -11, right: -11 } : corner === 'bl' ? { bottom: -11, left: -11 } : { bottom: -11, right: -11 })
                                  }} />
                              ))}
                            </div>
                          );
                        })}
                        {Array.from(checkedGiftIds).map(id => {
                          const anim = animations.find(a => a.id === id);
                          if (!anim || !anim.show_gift_image) return null;
                          const boxKey = `gift-${id}`;
                          const pos = pendingPos.get(boxKey) ?? { x: anim.gift_img_x ?? 5, y: anim.gift_img_y ?? 5, w: anim.gift_img_w ?? 20, h: anim.gift_img_h ?? 20 };
                          return (
                            <div key={boxKey} style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`, zIndex: 3, border: '1.5px dashed #f59e0b', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.14)', touchAction: 'none' }}>
                              <span style={{ fontSize: 24, pointerEvents: 'none' }}>🎁</span>
                              <div onTouchStart={e => startTouchFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, zIndex: 3 }} />
                              {(['tl','tr','bl','br'] as const).map(corner => (
                                <div key={corner} onTouchStart={e => startTouchFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                  style={{ position: 'absolute', width: 22, height: 22, background: '#f59e0b', border: '2px solid #fef3c7', borderRadius: 6, zIndex: 4,
                                    ...(corner === 'tl' ? { top: -11, left: -11 } : corner === 'tr' ? { top: -11, right: -11 } : corner === 'bl' ? { bottom: -11, left: -11 } : { bottom: -11, right: -11 })
                                  }} />
                              ))}
                            </div>
                          );
                        })}
                        {showSafeZone && (
                          <div style={{ position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none' }}>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '12%', background: 'rgba(239,68,68,0.22)', borderBottom: '1px dashed rgba(239,68,68,0.7)' }} />
                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '24%', background: 'rgba(239,68,68,0.22)', borderTop: '1px dashed rgba(239,68,68,0.7)' }} />
                            <div style={{ position: 'absolute', top: '12%', bottom: '24%', left: '6%', right: '6%', border: '2px dashed rgba(34,197,94,0.85)', borderRadius: 4 }} />
                          </div>
                        )}
                        {checkedAnimIds.size === 0 && (
                          <div style={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: 13 }}>Aucune animation cochée</div>
                        )}
                      </div>
                    </div>
                  )}

                  {fullscreenMobileTab === 'animations' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {animations.map(a => {
                        const isChecked = checkedAnimIds.has(a.id);
                        const k = `anim-${a.id}`;
                        const isRef = refKey === k;
                        return (
                          <React.Fragment key={a.id}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: isChecked ? '#111827' : '#080d14', border: `1px solid ${isChecked ? '#334155' : '#1e293b'}`, borderRadius: 8, padding: 8 }}>
                            <input type="checkbox" checked={isChecked} onChange={e => setCheckedAnimIds(prev => { const n = new Set(prev); e.target.checked ? n.add(a.id) : n.delete(a.id); return n; })} />
                            <span style={{ color: isChecked ? '#e2e8f0' : '#64748b', fontSize: 12, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                            <button onClick={() => { if (!checkedAnimIds.has(a.id)) setCheckedAnimIds(prev => new Set(prev).add(a.id)); setRefKey(isRef ? null : k); }}
                              style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${isRef ? '#fbbf24' : '#1e293b'}`, background: isRef ? '#422006' : '#111827', color: isRef ? '#fbbf24' : '#94a3b8', fontSize: 11 }}>
                              Réf.
                            </button>
                          </div>
                          {!!a.show_gift_image && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 18, marginTop: -2, background: checkedGiftIds.has(a.id) ? '#111827' : '#080d14', border: `1px solid ${checkedGiftIds.has(a.id) ? '#854d0e' : '#1e293b'}`, borderRadius: 8, padding: 8 }}>
                              <input type="checkbox" checked={checkedGiftIds.has(a.id)} onChange={e => setCheckedGiftIds(prev => { const n = new Set(prev); e.target.checked ? n.add(a.id) : n.delete(a.id); return n; })} />
                              <span style={{ color: checkedGiftIds.has(a.id) ? '#fbbf24' : '#64748b', fontSize: 12, flex: 1 }}>Cadeau</span>
                              <button onClick={() => { if (!checkedGiftIds.has(a.id)) setCheckedGiftIds(prev => new Set(prev).add(a.id)); const giftKey = `gift-${a.id}`; setRefKey(refKey === giftKey ? null : giftKey); }}
                                style={{ padding: '5px 8px', borderRadius: 6, border: `1px solid ${refKey === `gift-${a.id}` ? '#fbbf24' : '#1e293b'}`, background: refKey === `gift-${a.id}` ? '#422006' : '#111827', color: refKey === `gift-${a.id}` ? '#fbbf24' : '#94a3b8', fontSize: 11 }}>
                                Réf.
                              </button>
                            </div>
                          )}
                          </React.Fragment>
                        );
                      })}
                      {animations.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                          <button onClick={() => setCheckedAnimIds(new Set(animations.map(a => a.id)))} style={{ flex: 1, padding: 8, borderRadius: 7, border: '1px solid #1e293b', background: '#111827', color: '#94a3b8' }}>Tout</button>
                          <button onClick={() => { setCheckedAnimIds(new Set()); setCheckedGiftIds(new Set()); setRefKey(null); }} style={{ flex: 1, padding: 8, borderRadius: 7, border: '1px solid #1e293b', background: '#111827', color: '#94a3b8' }}>Aucun</button>
                        </div>
                      )}
                    </div>
                  )}

                  {fullscreenMobileTab === 'position' && (
                    <div>
                      {sectionTitleMobile('Aligner')}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        {toolBtn('← G', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, x: 0 }), checkedKeys))}
                        {toolBtn('⊕ H', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, x: (100 - p.w) / 2 }), checkedKeys))}
                        {toolBtn('D →', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, x: 100 - p.w }), checkedKeys))}
                        {toolBtn('↑ H', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, y: 0 }), checkedKeys))}
                        {toolBtn('⊕ V', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, y: (100 - p.h) / 2 }), checkedKeys))}
                        {toolBtn('↓ B', checkedKeys.length === 0, () => bulkAlignPending(p => ({ ...p, y: 100 - p.h }), checkedKeys))}
                      </div>
                      {sectionTitleMobile('Disposer')}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        {toolBtn('Côte à côte', checkedKeys.length < 2, () => bulkArrangePending('row', checkedKeys))}
                        {toolBtn('En colonne', checkedKeys.length < 2, () => bulkArrangePending('col', checkedKeys))}
                      </div>
                      {sectionTitleMobile('Taille et position')}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                        {toolBtn(refPos ? `Même taille (${Math.round(refPos.w)}×${Math.round(refPos.h)}%)` : 'Même taille', !refPos || checkedKeys.length === 0, () => { if (refPos) bulkAlignPending(p => ({ ...p, w: refPos.w, h: refPos.h }), checkedKeys.filter(k => k !== refKey)); })}
                        {toolBtn(refPos ? `Même place (${Math.round(refPos.x)}, ${Math.round(refPos.y)}%)` : 'Même place', !refPos || checkedKeys.length === 0, () => { if (refPos) bulkAlignPending(p => ({ ...p, x: refPos.x, y: refPos.y }), checkedKeys.filter(k => k !== refKey)); })}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: '#080d14', borderTop: '1px solid #1e293b', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <button onClick={saveAllPending} disabled={!pendingDirty || savingPending || !isAdmin}
                      style={{ padding: 9, borderRadius: 8, border: `1px solid ${pendingDirty && isAdmin ? '#854d0e' : '#1e293b'}`, background: pendingDirty && isAdmin ? '#1c1007' : '#111827', color: pendingDirty && isAdmin ? '#fbbf24' : '#475569', fontWeight: 700 }}>
                      {savingPending ? 'Sauvegarde…' : `Sauvegarder (${saveCount})`}
                    </button>
                    <button onClick={() => setIsFullscreen(false)} style={{ padding: 9, borderRadius: 8, border: '1px solid #1e293b', background: '#111827', color: '#cbd5e1' }}>Fermer</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    {(['theme', 'animations', 'position'] as const).map(tab => (
                      <button key={tab} onClick={() => setFullscreenMobileTab(tab)}
                        style={{ padding: 8, borderRadius: 8, border: `1px solid ${fullscreenMobileTab === tab ? '#2563eb' : '#1e293b'}`, background: fullscreenMobileTab === tab ? '#172554' : '#111827', color: fullscreenMobileTab === tab ? '#bfdbfe' : '#94a3b8', fontSize: 12, fontWeight: fullscreenMobileTab === tab ? 700 : 500 }}>
                        {tab === 'theme' ? 'Thème' : tab === 'animations' ? 'Animations' : 'Position'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

        {/* Modals (same as desktop) */}
        {showThemeForm && (
          <Modal title={editingTheme ? 'Modifier le thème' : 'Nouveau thème'} onClose={() => setShowThemeForm(false)}>
            <LightField label="Nom du thème">
              <input style={inputLight} value={themeForm.name} placeholder="ex: Haribo"
                onChange={e => { const name = e.target.value; setThemeForm(f => ({ ...f, name, slug: editingTheme ? f.slug : slugify(name) })); }} />
            </LightField>
            {!editingTheme && (
              <LightField label="Identifiant unique (slug)">
                <input style={inputLight} value={themeForm.slug} placeholder="ex: haribo"
                  onChange={e => setThemeForm(f => ({ ...f, slug: e.target.value }))} />
              </LightField>
            )}
            <LightField label="Illustration">
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputLight, flex: 1 }} value={themeForm.image_url} placeholder="URL d'une image (optionnel)"
                  onChange={e => { setThemeForm(f => ({ ...f, image_url: e.target.value })); setImagePreview(e.target.value); setImageFile(null); }} />
                <button onClick={() => imageRef.current?.click()} style={btnSecondary}>📁 Upload</button>
              </div>
              <input ref={imageRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (!f) return; setImageFile(f); setThemeForm(tf => ({ ...tf, image_url: '' })); const reader = new FileReader(); reader.onload = ev => setImagePreview(ev.target?.result as string); reader.readAsDataURL(f); e.target.value = ''; }} />
            </LightField>
            {imagePreview && <div style={{ display: 'flex', justifyContent: 'center' }}><img src={imagePreview} alt="preview" style={{ width: 60, height: 90, objectFit: 'cover', borderRadius: 7, border: '1px solid #e2e8f0' }} onError={e => (e.target as HTMLImageElement).style.display = 'none'} /></div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btnGhost} onClick={() => setShowThemeForm(false)}>Annuler</button>
              <button style={btnPrimary} onClick={saveTheme}>{editingTheme ? 'Enregistrer' : 'Créer'}</button>
            </div>
          </Modal>
        )}
        {showAnimForm && (
          <Modal title={editingAnim ? "Modifier l'animation" : 'Nouvelle animation'} onClose={() => setShowAnimForm(false)}>
            {animForm.animation_type !== 'text_dynamic' && (
              <LightField label="Nom (label affiché)">
                <input style={inputLight} value={animForm.name} placeholder="ex: Rose, Explosion…"
                  onChange={e => setAnimForm(f => ({ ...f, name: e.target.value }))} />
              </LightField>
            )}
            {animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && (
              <LightField label="Type d'animation">
                <select style={inputLight} value={animForm.animation_type}
                  onChange={e => setAnimForm(f => ({ ...f, animation_type: e.target.value as AnimFormState['animation_type'] }))}>
                  <option value="video">🎬 Vidéo</option>
                  <option value="audio">🔊 Son (audio)</option>
                  <option value="text">📝 Texte (statique)</option>
                  <option value="text_dynamic">⌨️ Texte dynamique (saisi en live)</option>
                </select>
              </LightField>
            )}
            {animForm.animation_type !== 'text_dynamic' && animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && (
              <LightField label="Déclenchement">
                <div style={{ display: 'flex', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                    padding: '8px 12px', borderRadius: 8, border: `1px solid ${animForm.trigger_type === 'auto' ? '#2563eb' : '#e2e8f0'}`,
                    background: animForm.trigger_type === 'auto' ? '#eff6ff' : '#f8fafc' }}>
                    <input type="radio" checked={animForm.trigger_type === 'auto'} onChange={() => setAnimForm(f => ({ ...f, trigger_type: 'auto' }))} />
                    🎁 Automatique (cadeau)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                    padding: '8px 12px', borderRadius: 8, border: `1px solid ${animForm.trigger_type === 'manual' ? '#7c3aed' : '#e2e8f0'}`,
                    background: animForm.trigger_type === 'manual' ? '#f5f3ff' : '#f8fafc' }}>
                    <input type="radio" checked={animForm.trigger_type === 'manual'} onChange={() => setAnimForm(f => ({ ...f, trigger_type: 'manual' }))} />
                    🎛️ Manuel (table de mixage)
                  </label>
                </div>
              </LightField>
            )}
            {animForm.animation_type !== 'text_dynamic' && animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && animForm.trigger_type === 'auto' && (
              <LightField label="">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${animForm.show_gift_image ? '#f59e0b' : '#e2e8f0'}`,
                  background: animForm.show_gift_image ? '#fffbeb' : '#f8fafc' }}>
                  <input type="checkbox" checked={animForm.show_gift_image}
                    onChange={e => setAnimForm(f => ({ ...f, show_gift_image: e.target.checked }))} />
                  🎁 Afficher l'image du cadeau sur l'overlay
                </label>
                {animForm.show_gift_image && (
                  <div style={{ color: '#92400e', fontSize: 11, marginTop: 4, padding: '4px 8px', background: '#fef3c7', borderRadius: 6, border: '1px solid #fde68a' }}>
                    Positionnez le cadre 🎁 dans la prévisualisation ci-dessous.
                  </div>
                )}
              </LightField>
            )}
            {animForm.animation_type === 'gift_grid' && (
              <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>🎁 Zone Cadeaux Games</div>
                <div style={{ fontSize: 11, color: '#78350f', background: '#fef3c7', borderRadius: 6, padding: '6px 8px' }}>
                  Affiche automatiquement tous les cadeaux assignés aux actions du jeu actif du streamer (via l'onglet Live interactif).
                </div>
                <LightField label="Nom de l'action — position">
                  <select style={inputLight} value={animForm.gift_label_pos}
                    onChange={e => setAnimForm(f => ({ ...f, gift_label_pos: e.target.value as AnimFormState['gift_label_pos'] }))}>
                    <option value="none">Aucun</option>
                    <option value="above">Au-dessus de l'image</option>
                    <option value="below">En-dessous de l'image</option>
                    <option value="left">À gauche de l'image</option>
                    <option value="right">À droite de l'image</option>
                    <option value="center">Centré sur l'image</option>
                  </select>
                </LightField>
                {animForm.gift_label_pos !== 'none' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <LightField label="Couleur du texte" style={{ flex: 1 }}>
                      <input type="color" value={animForm.gift_label_color}
                        onChange={e => setAnimForm(f => ({ ...f, gift_label_color: e.target.value }))}
                        style={{ width: '100%', height: 32, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                    </LightField>
                    <LightField label="Taille (px)" style={{ flex: 1 }}>
                      <input type="number" style={inputLight} min={8} max={48} value={animForm.gift_label_size}
                        onChange={e => setAnimForm(f => ({ ...f, gift_label_size: Number(e.target.value) }))} />
                    </LightField>
                  </div>
                )}
                <LightField label="Colonnes (0 = automatique)">
                  <input type="number" style={inputLight} min={0} max={10} value={animForm.gift_cols}
                    onChange={e => setAnimForm(f => ({ ...f, gift_cols: Number(e.target.value) }))} />
                </LightField>
                <div style={{ color: '#92400e', fontSize: 11 }}>
                  💡 La position et la taille de la zone se règlent dans la prévisualisation.
                </div>
              </div>
            )}
            {animForm.animation_type === 'queue' ? (
              <QueueFields form={animForm} set={setAnimForm} files={animFiles} onUpload={uploadQueueAsset} />
            ) : animForm.animation_type === 'mobile_comments' ? (
              <MobileCommentsFields form={animForm} set={setAnimForm} />
            ) : animForm.animation_type === 'raffle' ? (
              <RaffleFields form={animForm} set={setAnimForm} files={animFiles} onUpload={uploadQueueAsset} />
            ) : animForm.animation_type === 'banner' ? (
              <BannerFields form={animForm} set={setAnimForm} />
            ) : (animForm.animation_type === 'video' || animForm.animation_type === 'audio') ? (
              <LightField label="Fichier animation">
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...inputLight, flex: 1 }} value={animForm.animation_file} onChange={e => setAnimForm(f => ({ ...f, animation_file: e.target.value }))}>
                    <option value="">— Choisir —</option>
                    {animFiles.filter(af => {
                      const ext = af.split('.').pop()?.toLowerCase() ?? '';
                      if (animForm.animation_type === 'video') return ['mp4','webm','mov'].includes(ext);
                      if (animForm.animation_type === 'audio') return ['mp3','ogg','wav','aac'].includes(ext);
                      return true;
                    }).map(af => {
                      // Afficher seulement le nom du fichier sans le préfixe de dossier
                      const displayName = af.includes('/') ? af.split('/').pop()! : af;
                      return <option key={af} value={af}>{displayName}</option>;
                    })}
                  </select>
                  <input ref={animFileRef} type="file" accept=".mp4,.webm,.mov,.mp3,.ogg,.wav,.aac" style={{ display: 'none' }} onChange={handleUploadAnimFile} />
                  <button onClick={() => animFileRef.current?.click()} style={btnSecondary}>{uploadingAnim ? '⏳' : '📁 Upload'}</button>
                </div>
              </LightField>
            ) : animForm.animation_type !== 'gift_grid' ? (
              <>
                {animForm.animation_type === 'text' && (
                  <LightField label="Texte à afficher">
                    <textarea style={{ ...inputLight, minHeight: 56, resize: 'vertical' }} value={animForm.text_content}
                      placeholder="ex: Bienvenue ! 🎉"
                      onChange={e => setAnimForm(f => ({ ...f, text_content: e.target.value }))} />
                  </LightField>
                )}
                {animForm.animation_type === 'text_dynamic' && (
                  <div style={{ color: '#64748b', fontSize: 12, padding: '6px 0' }}>ℹ️ Le streamer saisit le texte en direct depuis son overlay mobile.</div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <LightField label="Taille (px)" style={{ flex: 1 }}>
                    <input type="number" style={inputLight} value={animForm.text_size} min={12} max={200}
                      onChange={e => setAnimForm(f => ({ ...f, text_size: Number(e.target.value) }))} />
                  </LightField>
                  <LightField label="Couleur" style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="color" value={animForm.text_color}
                        onChange={e => setAnimForm(f => ({ ...f, text_color: e.target.value }))}
                        style={{ width: 36, height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                      <input style={{ ...inputLight, flex: 1 }} value={animForm.text_color}
                        onChange={e => setAnimForm(f => ({ ...f, text_color: e.target.value }))} />
                    </div>
                  </LightField>
                </div>
                <LightField label="Police">
                  <select style={inputLight} value={animForm.text_font}
                    onChange={e => setAnimForm(f => ({ ...f, text_font: e.target.value }))}>
                    <option value="sans-serif">Sans-serif</option>
                    <option value="serif">Serif</option>
                    <option value="Impact">Impact</option>
                    <option value="Arial Black">Arial Black</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Comic Sans MS">Comic Sans MS</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Trebuchet MS">Trebuchet MS</option>
                  </select>
                </LightField>
                <div style={{ display: 'flex', gap: 8 }}>
                  <LightField label="Effet" style={{ flex: 1 }}>
                    <select style={inputLight} value={animForm.text_effect}
                      onChange={e => setAnimForm(f => ({ ...f, text_effect: e.target.value }))}>
                      <option value="fade">Fondu</option>
                      <option value="scroll-left">Défilement gauche</option>
                      <option value="scroll-right">Défilement droite</option>
                      <option value="scroll-up">Défilement haut</option>
                      <option value="scroll-down">Défilement bas</option>
                      <option value="typewriter">Machine à écrire</option>
                      <option value="bounce">Rebond</option>
                      <option value="zoom">Zoom</option>
                    </select>
                  </LightField>
                  <LightField label="Boucles" style={{ flex: 1 }}>
                    <input type="number" style={inputLight} value={animForm.text_loops} min={1} max={20}
                      onChange={e => setAnimForm(f => ({ ...f, text_loops: Number(e.target.value) }))} />
                  </LightField>
                  <LightField label="Durée/boucle (s)" style={{ flex: 1 }}>
                    <input type="number" style={inputLight} value={animForm.text_duration} min={1} max={60} step={0.5}
                      onChange={e => setAnimForm(f => ({ ...f, text_duration: Number(e.target.value) }))} />
                  </LightField>
                </div>
              </>
            ) : null}
            <div style={{ color: '#94a3b8', fontSize: 12 }}>La position se règle dans la prévisualisation.</div>
            {animError && <div style={{ color: '#dc2626', fontSize: 12, background: '#fef2f2', borderRadius: 6, padding: '7px 10px', border: '1px solid #fecaca' }}>{animError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btnGhost} onClick={() => setShowAnimForm(false)}>Annuler</button>
              <button style={btnPrimary} onClick={saveAnim}>{editingAnim ? 'Enregistrer' : 'Ajouter'}</button>
            </div>
          </Modal>
        )}
      </div>
    );
    }
    // Sinon → liste simple
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {headerSlot && <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '10px 12px 0', flexShrink: 0 }}>{headerSlot}</div>}
        {/* Toolbar */}
        <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, flexShrink: 0 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" style={{ ...inputLight, paddingLeft: 28, fontSize: 12 }} />
          </div>
          {isAdmin && <button onClick={openAddTheme} style={btnPrimary}>+ Nouveau</button>}
        </div>

        {/* Simple list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filteredThemes.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Aucun résultat' : 'Aucun thème'}
            </div>
          )}
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredThemes.map(t => (
            <div key={t.id}
              onClick={() => { setSelected(t); loadAnimations(t.id); setSelectedAnim(null); setEditPos(null); }}
              style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              {t.image_url ? (
                <img src={t.image_url} alt={t.name} loading="lazy"
                  style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              ) : (
                <div style={{ width: '100%', height: 90, background: '#f5f3ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🎨</div>
              )}
              <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}>{t.name}</div>
                  <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{(t as any).animation_count ?? 0} animation{((t as any).animation_count ?? 0) !== 1 ? 's' : ''}</div>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={e => openEditTheme(t, e)} style={iconBtn} title="Modifier">✏️</button>
                    <button onClick={e => handleDeleteTheme(t, e)} style={{ ...iconBtn, color: '#dc2626' }} title="Supprimer">🗑</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Modals */}
        {showThemeForm && (
          <Modal title={editingTheme ? 'Modifier le thème' : 'Nouveau thème'} onClose={() => setShowThemeForm(false)}>
            <LightField label="Nom du thème">
              <input style={inputLight} value={themeForm.name} placeholder="ex: Haribo"
                onChange={e => { const name = e.target.value; setThemeForm(f => ({ ...f, name, slug: editingTheme ? f.slug : slugify(name) })); }} />
            </LightField>
            {!editingTheme && (
              <LightField label="Identifiant unique (slug)">
                <input style={inputLight} value={themeForm.slug} placeholder="ex: haribo"
                  onChange={e => setThemeForm(f => ({ ...f, slug: e.target.value }))} />
              </LightField>
            )}
            <LightField label="Illustration">
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputLight, flex: 1 }} value={themeForm.image_url} placeholder="URL d'une image (optionnel)"
                  onChange={e => { setThemeForm(f => ({ ...f, image_url: e.target.value })); setImagePreview(e.target.value); setImageFile(null); }} />
                <button onClick={() => imageRef.current?.click()} style={btnSecondary}>📁 Upload</button>
              </div>
              <input ref={imageRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (!f) return; setImageFile(f); setThemeForm(tf => ({ ...tf, image_url: '' })); const reader = new FileReader(); reader.onload = ev => setImagePreview(ev.target?.result as string); reader.readAsDataURL(f); e.target.value = ''; }} />
            </LightField>
            {imagePreview && <div style={{ display: 'flex', justifyContent: 'center' }}><img src={imagePreview} alt="preview" style={{ width: 60, height: 90, objectFit: 'cover', borderRadius: 7, border: '1px solid #e2e8f0' }} onError={e => (e.target as HTMLImageElement).style.display = 'none'} /></div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button style={btnGhost} onClick={() => setShowThemeForm(false)}>Annuler</button>
              <button style={btnPrimary} onClick={saveTheme}>{editingTheme ? 'Enregistrer' : 'Créer'}</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Liste thèmes ─────────────────────────────── */}
      <div style={{ width: 320, flexShrink: 0, background: '#f8fafc', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {headerSlot && <div style={{ padding: '14px 16px 0', flexShrink: 0 }}>{headerSlot}</div>}
        {/* Toolbar */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ color: '#475569', fontSize: 13, fontWeight: 600, flex: 1 }}>
              {themes.length} thème{themes.length !== 1 ? 's' : ''}
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
            </svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" style={{ ...inputLight, paddingLeft: 28, fontSize: 12 }} />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredThemes.map(t => {
            const isActive = selected?.id === t.id;
            return (
              <div key={t.id} onClick={() => { setSelected(t); setSelectedAnim(null); setEditPos(null); }}
                style={{ background: isActive ? '#eff6ff' : '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                  border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`, boxShadow: isActive ? '0 0 0 3px rgba(37,99,235,0.08)' : '0 1px 2px rgba(0,0,0,0.04)',
                  display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s' }}>
                {t.image_url ? (
                  <img src={t.image_url} alt={t.name} loading="lazy"
                    style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#f1f5f9' }}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                  <div style={{ width: '100%', height: 90, background: '#f5f3ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🎨</div>
                )}
                <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: isActive ? '#2563eb' : '#0f172a', fontWeight: 700, fontSize: 13, lineHeight: 1.3, wordBreak: 'break-word' }}>{t.name}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{(t as any).animation_count ?? 0} animation{((t as any).animation_count ?? 0) !== 1 ? 's' : ''}</div>
                  </div>
                  {isAdmin && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      <button onClick={e => openEditTheme(t, e)} style={iconBtn} title="Modifier">
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button onClick={e => handleDeleteTheme(t, e)} style={{ ...iconBtn, color: '#dc2626' }} title="Supprimer">
                        <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {filteredThemes.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              {search ? 'Aucun résultat' : 'Aucun thème'}
            </div>
          )}
        </div>
        {isAdmin && (
          <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
            <button onClick={openAddTheme} style={{ ...btnPrimary, width: '100%', justifyContent: 'center' }}>+ Nouveau th&egrave;me</button>
          </div>
        )}
      </div>

      {/* ── Détail thème ──────────────────────────────── */}
      {selected ? (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* Animations / Streamers */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tab bar */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <button onClick={() => { setSelected(null); setSelectedAnim(null); setEditPos(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', color: '#64748b', fontSize: 12, cursor: 'pointer', padding: '0 12px 0 4px', height: 44, flexShrink: 0 }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Thèmes
                </button>
                <div style={{ width: 1, height: 16, background: '#e2e8f0', marginRight: 12, flexShrink: 0 }} />
                <span style={{ color: '#0f172a', fontWeight: 600, fontSize: 14, paddingRight: 16, borderRight: '1px solid #f1f5f9', marginRight: 16 }}>{selected.name}</span>
                <DetailTab active={detailTab === 'animations'} onClick={() => setDetailTab('animations')}>Animations ({animations.length})</DetailTab>
                <DetailTab active={detailTab === 'streamers'} onClick={() => setDetailTab('streamers')}>Streamers</DetailTab>
              </div>
              {isAdmin && detailTab === 'animations' && (
                <div style={{ position: 'relative' }} ref={typeMenuRef}>
                  <button onClick={openAddAnim} style={btnPrimary}>+ Animation ▾</button>
                  {showTypeMenu && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 210, zIndex: 60, overflow: 'hidden' }}>
                      <button onClick={() => openAddAnimWithType('video')} style={dropdownItem}>🎬 Vidéo</button>
                      <button onClick={() => openAddAnimWithType('audio')} style={dropdownItem}>🔊 Son (audio)</button>
                      <button onClick={() => openAddAnimWithType('text')} style={dropdownItem}>📝 Texte (statique)</button>
                      <button onClick={() => openAddAnimWithType('text_dynamic')} style={dropdownItem}>⌨️ Texte dynamique</button>
                      <button onClick={() => openAddAnimWithType('banner')} style={dropdownItem}>🪧 Bannière</button>
                      <button onClick={() => openAddAnimWithType('gift_grid')} style={dropdownItem}>🎁 Cadeaux Games</button>
                    <button onClick={() => openAddAnimWithType('queue')} style={dropdownItem}>📋 File d'attente</button>
                    <button onClick={() => openAddAnimWithType('raffle')} style={dropdownItem}>Tirage au sort</button>
                    <button onClick={() => openAddAnimWithType('mobile_comments')} style={{ ...dropdownItem, borderBottom: 'none' }}>Commentaires mobile</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f8fafc' }}>
              {detailTab === 'animations' && (
                animations.length === 0 ? (
                  <div style={{ textAlign: 'center', paddingTop: 60, color: '#94a3b8', fontSize: 13 }}>
                    Aucune animation.{isAdmin ? ' Clique sur "+ Animation".' : ''}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {animations.map(a => {
                      const isActive = selectedAnim?.id === a.id;
                      return (
                        <div key={a.id} onClick={() => selectAnim(a)}
                          style={{ background: isActive ? '#eff6ff' : '#fff', border: `1px solid ${isActive ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'all 0.12s' }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: isActive ? '#dbeafe' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, border: '1px solid #e2e8f0' }}>
                            {a.animation_type === 'gift_grid' ? '🎁' : a.animation_type === 'queue' ? '📋' : a.animation_type === 'banner' ? '🪧' : a.animation_type === 'text' ? '📝' : a.animation_type === 'text_dynamic' ? '⌨️' : a.animation_type === 'audio' ? '🔊' : '🎬'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: isActive ? '#2563eb' : '#0f172a', fontWeight: 600, fontSize: 13 }}>{a.name}</div>
                            <div style={{ color: '#94a3b8', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
                              {a.animation_type === 'gift_grid' ? '🎁 Zone Cadeaux Games' : a.animation_type === 'queue' ? (a.name || "File d'attente") : a.animation_type === 'banner' ? 'Bannière défilante' : a.animation_type === 'text' ? (a.text_content || '(texte vide)') : a.animation_type === 'text_dynamic' ? 'Texte dynamique (saisi en live)' : a.animation_file}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                            <span style={{ ...posBadge, background: a.trigger_type === 'manual' ? '#f5f3ff' : '#f0f9ff', color: a.trigger_type === 'manual' ? '#7c3aed' : '#2563eb', border: `1px solid ${a.trigger_type === 'manual' ? '#ddd6fe' : '#bfdbfe'}` }}>
                              {a.trigger_type === 'manual' ? '🎛️' : '🎁'}
                            </span>
                            <span style={posBadge}>x:{Math.round(a.pos_x)}% y:{Math.round(a.pos_y)}%</span>
                            <span style={posBadge}>{Math.round(a.pos_w)}×{Math.round(a.pos_h)}%</span>
                          </div>
                          {isAdmin && (
                            <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>
                              <button onClick={e => openEditAnim(a, e)} style={iconBtn}>
                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                              </button>
                              <button onClick={e => handleDeleteAnim(a, e)} style={{ ...iconBtn, color: '#dc2626' }}>
                                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {detailTab === 'static' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {staticElements.length === 0 && (
                    <div style={{ textAlign: 'center', paddingTop: 60, color: '#94a3b8', fontSize: 13 }}>
                      Aucune image permanente.{isAdmin ? ' Clique sur "+ Image permanente".' : ''}
                    </div>
                  )}
                  {staticElements.map(el => (
                    <div key={el.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Miniature */}
                      <div style={{ width: 40, height: 40, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9', flexShrink: 0, border: '1px solid #e2e8f0' }}>
                        <img src={`/api/animation-files/${el.image_file}`} alt={el.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 13 }}>{el.name}</div>
                        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{el.image_file}</div>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, border: '1px solid', ...(el.layer === 'above' ? { background: '#f0fdf4', color: '#16a34a', borderColor: '#bbf7d0' } : { background: '#fefce8', color: '#ca8a04', borderColor: '#fde68a' }) }}>
                        {el.layer === 'above' ? '↑ Dessus' : '↓ Dessous'}
                      </span>
                      <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        x:{Math.round(el.pos_x)}% y:{Math.round(el.pos_y)}%
                      </span>
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 3 }}>
                          <button onClick={() => openEditStatic(el)} style={iconBtn}>
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => deleteStaticElem(el.id)} style={{ ...iconBtn, color: '#dc2626' }}>
                            <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {detailTab === 'streamers' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Barre de recherche */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
                    <input
                      value={streamerSearch}
                      onChange={e => setStreamerSearch(e.target.value)}
                      placeholder="Rechercher un streamer…"
                      style={{ ...inputLight, paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
                    />
                    {streamerSearch && (
                      <button onClick={() => setStreamerSearch('')}
                        style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {visibleThemeStreamers.map(s => {
                    const ts = themeStreamers.find(x => x.id === s.id);
                    const has = ts ? ts.has_theme === 1 : false;
                    return (
                      <div key={s.id} style={{ background: '#fff', border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: has ? '#ede9fe' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: has ? '#7c3aed' : '#94a3b8', flexShrink: 0, overflow: 'hidden' }}>
                          {s.avatar_url
                            ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            : s.display_name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 13 }}>{s.display_name}</div>
                          <div style={{ color: '#94a3b8', fontSize: 11 }}>@{s.username}</div>
                        </div>
                        {isAdmin && (
                          <button onClick={() => toggleStreamer(s.id, has)}
                            style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, background: has ? '#eff6ff' : '#f8fafc', color: has ? '#2563eb' : '#64748b', fontSize: 12, cursor: 'pointer', fontWeight: has ? 600 : 400 }}>
                            {has ? '✓ Assigné' : '+ Assigner'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {visibleThemeStreamers.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 13, padding: '20px 0' }}>
                      Aucun streamer trouvé pour « {streamerSearch} »
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Prévisualisation ── */}
          <div style={{ width: 270, flexShrink: 0, background: '#fff', borderLeft: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ padding: '14px 14px 10px', flexShrink: 0 }}>
              {/* Header avec bouton plein écran */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Prévisualisation</div>
                <button onClick={() => { initPendingPos(animations); setCheckedAnimIds(new Set()); setCheckedGiftIds(new Set()); setRefKey(null); setIsFullscreen(true); }} title="Plein écran"
                  style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: '#64748b', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, lineHeight: 1 }}>
                  ⛶ <span>Plein écran</span>
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {isFullscreen ? (
                  <div style={{ width: 190, height: 338, background: '#111', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #333' }}>
                    <span style={{ color: '#555', fontSize: 10, textAlign: 'center' }}>Mode plein écran<br/>actif</span>
                  </div>
                ) : (
                  <div ref={previewRef} style={{ position: 'relative', width: 190, height: 338, background: '#0a0a0a', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', flexShrink: 0, userSelect: 'none' }}>
                    {currentOverlayUrl && (
                      <img src={currentOverlayUrl} alt="overlay" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: 1 }} />
                    )}
                    {selectedAnim && editPos && selectedAnim.animation_type !== 'gift_grid' && (
                      <div style={{ position: 'absolute', left: `${editPos.x}%`, top: `${editPos.y}%`, width: `${editPos.w}%`, height: `${editPos.h}%`, zIndex: 2, boxSizing: 'border-box', border: `1.5px dashed ${isDirty ? '#f59e0b' : 'rgba(37,99,235,0.8)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {(selectedAnim.animation_type === 'text' || selectedAnim.animation_type === 'text_dynamic') ? (
                          <div style={{ color: selectedAnim.text_color ?? '#fff', fontSize: Math.min((selectedAnim.text_size ?? 48) * 0.35, 14), fontWeight: 700, textAlign: 'center', textShadow: '1px 1px 3px rgba(0,0,0,0.8)', padding: 4, overflow: 'hidden', wordBreak: 'break-word', lineHeight: 1.2, width: '100%' }}>
                            {selectedAnim.animation_type === 'text_dynamic' ? '⌨️ Dynamique' : (selectedAnim.text_content || '📝 Texte')}
                          </div>
                        ) : selectedAnim.animation_type === 'audio' ? (
                          <div style={{ color: '#fff', fontSize: 22, textAlign: 'center' }}>🔊</div>
                        ) : selectedAnim.animation_type === 'queue' ? (
                          <div style={{ color: selectedAnim.text_color ?? '#fff', fontSize: 12, fontWeight: 700, textAlign: 'center', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>📋 Liste</div>
                        ) : (
                          <video ref={videoRef} key={selectedAnim.animation_file ?? ''} src={animationFileUrl(selectedAnim.animation_file ?? '')}
                            loop playsInline preload="auto" draggable={false}
                            onLoadedData={() => { const v = videoRef.current; if (!v) return; v.pause(); setIsPlaying(false); }}
                            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none' }} />
                        )}
                        {isAdmin && <div onMouseDown={e => startDrag(e, 'move')} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 3 }} />}
                        {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                          <div key={corner} onMouseDown={e => startDrag(e, `resize-${corner}` as DragState['mode'])}
                            style={{ position: 'absolute', width: 10, height: 10, background: isDirty ? '#f59e0b' : '#2563eb', borderRadius: 2, zIndex: 4, cursor: 'nwse-resize',
                              ...(corner === 'tl' ? { top: -5, left: -5 } : corner === 'tr' ? { top: -5, right: -5, cursor: 'nesw-resize' } : corner === 'bl' ? { bottom: -5, left: -5, cursor: 'nesw-resize' } : { bottom: -5, right: -5 })
                            }} />
                        ))}
                      </div>
                    )}
                    {/* Gift_grid zones — toutes affichées en permanence */}
                    {animations.filter(a => a.animation_type === 'gift_grid').map(a => {
                      const isActive = selectedAnim?.id === a.id;
                      const pos = isActive && editPos ? editPos : { x: a.pos_x, y: a.pos_y, w: a.pos_w, h: a.pos_h };
                      const labelPos = a.gift_label_pos ?? 'none';
                      return (
                        <div key={`gg-${a.id}`}
                          onClick={() => selectAnim(a)}
                          style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                            zIndex: isActive ? 2 : 1, boxSizing: 'border-box',
                            border: `1.5px dashed ${isActive ? (isDirty ? '#f59e0b' : '#ca8a04') : 'rgba(202,138,4,0.5)'}`,
                            background: 'rgba(234,179,8,0.12)', borderRadius: 3,
                            display: 'flex', flexWrap: 'wrap', overflow: 'hidden', cursor: 'pointer' }}>
                          {[1,2,3,4].map(i => (
                            <div key={i} style={{ width: '50%', height: '50%', display: 'flex', flexDirection: 'column',
                              alignItems: 'center', justifyContent: 'center', padding: '3%', boxSizing: 'border-box' }}>
                              <div style={{ background: '#fbbf24', borderRadius: '50%', width: '55%', aspectRatio: '1/1', opacity: 0.4 }} />
                              {labelPos !== 'none' && (
                                <span style={{ fontSize: 4, color: '#92400e', marginTop: 1, lineHeight: 1 }}>Action</span>
                              )}
                            </div>
                          ))}
                          <div style={{ position: 'absolute', bottom: 1, right: 2, fontSize: 5, color: '#92400e', fontWeight: 700 }}>🎁</div>
                          {isActive && isAdmin && <div onMouseDown={e => startDrag(e, 'move')} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 3 }} />}
                          {isActive && isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                            <div key={corner} onMouseDown={e => startDrag(e, `resize-${corner}` as DragState['mode'])}
                              style={{ position: 'absolute', width: 8, height: 8, background: isDirty ? '#f59e0b' : '#ca8a04', borderRadius: 2, zIndex: 4, cursor: 'nwse-resize',
                                ...(corner === 'tl' ? { top: -4, left: -4 } : corner === 'tr' ? { top: -4, right: -4, cursor: 'nesw-resize' } : corner === 'bl' ? { bottom: -4, left: -4, cursor: 'nesw-resize' } : { bottom: -4, right: -4 })
                              }} />
                          ))}
                        </div>
                      );
                    })}
                    {selectedAnim && selectedAnim.show_gift_image && editGiftPos && (
                      <div style={{ position: 'absolute', left: `${editGiftPos.x}%`, top: `${editGiftPos.y}%`, width: `${editGiftPos.w}%`, height: `${editGiftPos.h}%`, zIndex: 3, boxSizing: 'border-box', border: '1.5px dashed rgba(245,158,11,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.1)' }}>
                        <span style={{ fontSize: 14, opacity: 0.8 }}>🎁</span>
                        {isAdmin && <div onMouseDown={e => startDrag(e, 'move', 'gift')} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 4 }} />}
                        {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                          <div key={corner} onMouseDown={e => startDrag(e, `resize-${corner}` as DragState['mode'], 'gift')}
                            style={{ position: 'absolute', width: 8, height: 8, background: '#f59e0b', borderRadius: 2, zIndex: 5, cursor: 'nwse-resize',
                              ...(corner === 'tl' ? { top: -4, left: -4 } : corner === 'tr' ? { top: -4, right: -4, cursor: 'nesw-resize' } : corner === 'bl' ? { bottom: -4, left: -4, cursor: 'nesw-resize' } : { bottom: -4, right: -4 })
                            }} />
                        ))}
                      </div>
                    )}
                    {!selectedAnim && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                        <span style={{ color: '#555', fontSize: 11, textAlign: 'center', lineHeight: 1.6 }}>Sélectionne<br/>une animation ←</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedAnim && editPos && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5, marginBottom: 6 }}>
                    {(['x','y','w','h'] as const).map(k => (
                      <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: '#94a3b8', fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{k === 'w' ? 'Largeur' : k === 'h' ? 'Hauteur' : k === 'x' ? 'Pos. X' : 'Pos. Y'}</span>
                        <input type="number" step={0.1} value={Math.round(editPos[k] * 10) / 10}
                          onChange={e => { setEditPos(p => p ? { ...p, [k]: parseFloat(e.target.value) || 0 } : p); setIsDirty(true); }}
                          style={{ ...inputLight, fontSize: 11, padding: '4px 7px', textAlign: 'center' }} />
                      </div>
                    ))}
                  </div>
                  {isDirty && isAdmin && (
                    <button onClick={() => editPos && savePosition(editPos)} disabled={saving}
                      style={{ width: '100%', padding: '7px', borderRadius: 6, border: '1px solid #fbbf24', background: '#fffbeb', color: saving ? '#94a3b8' : '#d97706', fontSize: 12, cursor: saving ? 'wait' : 'pointer', fontWeight: 600 }}>
                      {saving ? 'Sauvegarde…' : '💾 Sauvegarder position'}
                    </button>
                  )}
                </div>
              )}

              {selectedAnim && selectedAnim.animation_type === 'video' && !isFullscreen && (
                <button onClick={() => { const v = videoRef.current; if (!v) return; if (isPlaying) { v.pause(); setIsPlaying(false); } else { v.play(); setIsPlaying(true); } }}
                  style={{ marginTop: 8, width: '100%', padding: '8px', borderRadius: 7, border: `1px solid ${isPlaying ? '#bbf7d0' : '#e2e8f0'}`, background: isPlaying ? '#f0fdf4' : '#f8fafc', color: isPlaying ? '#16a34a' : '#64748b', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {isPlaying ? '⏸ Pause' : '▶ Aperçu'}
                </button>
              )}
            </div>

            {/* ── Modal plein écran 3 panneaux ── */}
            {isFullscreen && (() => {
              const PALETTE = ['#3b82f6','#10b981','#22d3ee','#ef4444','#8b5cf6','#ec4899','#84cc16','#f97316','#6366f1','#14b8a6'];
              const sectionTitle = (t: string) => (
                <div style={{ color: '#475569', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, paddingTop: 8, borderTop: '1px solid #1e293b', marginTop: 4 }}>{t}</div>
              );
              const checkedKeys = [
                ...Array.from(checkedAnimIds).map(id => `anim-${id}`),
                ...Array.from(checkedGiftIds).map(id => `gift-${id}`),
              ];
              const refPos = refKey ? pendingPos.get(refKey) : null;
              const saveCount = Array.from(pendingPos.keys()).filter(k => k.startsWith('anim-')).length;
              return (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#0d1117', display: 'flex', flexDirection: 'row' }}>

                  {/* ── Panneau gauche : liste avec cases à cocher ── */}
                  <div style={{ width: 220, flexShrink: 0, background: '#080d14', borderRight: '1px solid #1e293b', display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '12px 8px' }}>
                    <div style={{ color: '#475569', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Animations du thème</div>
                    {animations.length === 0 && <div style={{ color: '#334155', fontSize: 10, textAlign: 'center', marginTop: 20 }}>Aucune animation</div>}
                    {animations.map((a, idx) => {
                      const color = PALETTE[idx % PALETTE.length];
                      const isAnimChecked = checkedAnimIds.has(a.id);
                      const isGiftChecked = checkedGiftIds.has(a.id);
                      const icon = a.animation_type === 'audio' ? '🔊' : a.animation_type === 'text' ? '📝' : a.animation_type === 'text_dynamic' ? '⌨️' : '🎬';
                      return (
                        <div key={a.id} style={{ marginBottom: 3 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 7px', borderRadius: 6, cursor: 'pointer', flex: 1, minWidth: 0,
                              background: isAnimChecked ? `${color}20` : 'transparent',
                              border: `1px solid ${isAnimChecked ? color : '#1e293b'}` }}>
                              <input type="checkbox" checked={isAnimChecked}
                                onChange={e => {
                                  setCheckedAnimIds(prev => { const n = new Set(prev); e.target.checked ? n.add(a.id) : n.delete(a.id); return n; });
                                }}
                                style={{ accentColor: color, cursor: 'pointer', flexShrink: 0 }} />
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{icon}</span>
                              <span style={{ color: isAnimChecked ? '#e2e8f0' : '#64748b', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontWeight: isAnimChecked ? 600 : 400 }}>{a.name}</span>
                              {refKey === `anim-${a.id}` && <span title="Référence taille" style={{ fontSize: 9, color, flexShrink: 0 }}>⭐</span>}
                            </label>
                            {a.animation_type === 'video' && a.animation_file && isAnimChecked && (
                              <button
                                title="Lire une fois"
                                onClick={() => {
                                  const el = fullscreenVideoRefs.current.get(a.id);
                                  if (!el) return;
                                  el.currentTime = 0;
                                  el.play().then(() => setPlayingAnimId(a.id)).catch(() => {});
                                }}
                                style={{ flexShrink: 0, width: 24, height: 24, borderRadius: 5, border: `1px solid ${playingAnimId === a.id ? color : '#1e293b'}`, background: playingAnimId === a.id ? `${color}30` : '#111827', color: playingAnimId === a.id ? color : '#64748b', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                                {playingAnimId === a.id ? '⏹' : '▶'}
                              </button>
                            )}
                            <button
                              title="Utiliser comme référence taille"
                              onClick={() => {
                                if (!checkedAnimIds.has(a.id)) setCheckedAnimIds(prev => new Set(prev).add(a.id));
                                const k = `anim-${a.id}`;
                                setRefKey(refKey === k ? null : k);
                              }}
                              style={{ flexShrink: 0, height: 24, borderRadius: 5, border: `1px solid ${refKey === `anim-${a.id}` ? color : '#1e293b'}`, background: refKey === `anim-${a.id}` ? `${color}30` : '#111827', color: refKey === `anim-${a.id}` ? color : '#64748b', cursor: 'pointer', fontSize: 10, padding: '0 6px' }}>
                              Réf.
                            </button>
                          </div>
                          {!!a.show_gift_image && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 7px 4px 22px', borderRadius: 6, cursor: 'pointer', marginTop: 2,
                              background: isGiftChecked ? 'rgba(245,158,11,0.15)' : 'transparent',
                              border: `1px solid ${isGiftChecked ? '#f59e0b' : '#1e293b'}` }}>
                              <input type="checkbox" checked={isGiftChecked}
                                onChange={e => setCheckedGiftIds(prev => { const n = new Set(prev); e.target.checked ? n.add(a.id) : n.delete(a.id); return n; })}
                                style={{ accentColor: '#f59e0b', cursor: 'pointer', flexShrink: 0 }} />
                              <span style={{ fontSize: 11, flexShrink: 0 }}>🎁</span>
                              <span style={{ color: isGiftChecked ? '#fbbf24' : '#475569', fontSize: 10, flex: 1 }}>Cadeau</span>
                              {refKey === `gift-${a.id}` && <span title="Référence taille" style={{ fontSize: 9, color: '#f59e0b', flexShrink: 0 }}>⭐</span>}
                              <button
                                title="Utiliser le cadeau comme référence taille"
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!checkedGiftIds.has(a.id)) setCheckedGiftIds(prev => new Set(prev).add(a.id));
                                  const k = `gift-${a.id}`;
                                  setRefKey(refKey === k ? null : k);
                                }}
                                style={{ flexShrink: 0, height: 22, borderRadius: 5, border: `1px solid ${refKey === `gift-${a.id}` ? '#f59e0b' : '#1e293b'}`, background: refKey === `gift-${a.id}` ? 'rgba(245,158,11,0.2)' : '#111827', color: refKey === `gift-${a.id}` ? '#fbbf24' : '#64748b', cursor: 'pointer', fontSize: 10, padding: '0 6px' }}>
                                Réf.
                              </button>
                            </label>
                          )}
                        </div>
                      );
                    })}
                    {animations.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 10, paddingTop: 8, borderTop: '1px solid #1e293b' }}>
                        <button onClick={() => { setCheckedAnimIds(new Set(animations.map(a => a.id))); setCheckedGiftIds(new Set(animations.filter(a => !!a.show_gift_image).map(a => a.id))); }}
                          style={{ flex: 1, padding: '4px', borderRadius: 5, fontSize: 10, border: '1px solid #1e293b', background: '#111827', color: '#64748b', cursor: 'pointer' }}>Tout</button>
                        <button onClick={() => { setCheckedAnimIds(new Set()); setCheckedGiftIds(new Set()); }}
                          style={{ flex: 1, padding: '4px', borderRadius: 5, fontSize: 10, border: '1px solid #1e293b', background: '#111827', color: '#64748b', cursor: 'pointer' }}>Aucun</button>
                      </div>
                    )}
                  </div>

                  {/* ── Zone canvas (centre) ── */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 16 }}>
                    <div ref={previewRefFull} style={{ position: 'relative', height: 'calc(100vh - 32px)', aspectRatio: '9/16', background: '#0a0a0a', borderRadius: 12, overflow: 'hidden', border: '1px solid #1e293b', userSelect: 'none', flexShrink: 0 }}>
                      {currentOverlayUrl && (
                        <img src={currentOverlayUrl} alt="overlay" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', zIndex: 1 }} />
                      )}

                      {/* Boîtes d'animations cochées */}
                      {Array.from(checkedAnimIds).map(id => {
                        const anim = animations.find(a => a.id === id);
                        if (!anim) return null;
                        const color = PALETTE[animations.indexOf(anim) % PALETTE.length];
                        const boxKey = `anim-${id}`;
                        const pos = pendingPos.get(boxKey) ?? { x: anim.pos_x, y: anim.pos_y, w: anim.pos_w, h: anim.pos_h };
                        const isRef = refKey === boxKey;
                        const icon = anim.animation_type === 'audio' ? '🔊' : anim.animation_type === 'text' ? '📝' : anim.animation_type === 'text_dynamic' ? '⌨️' : anim.animation_type === 'queue' ? '📋' : '🎬';
                        return (
                          <div key={boxKey}
                            style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                              zIndex: 2, boxSizing: 'border-box', cursor: 'default', overflow: 'hidden',
                              border: `2px ${isRef ? 'dashed' : 'solid'} ${color}` }}>
                            {/* Contenu de l'animation */}
                            {anim.animation_type === 'video' && anim.animation_file ? (
                              <video
                                ref={el => { if (el) fullscreenVideoRefs.current.set(anim.id, el); else fullscreenVideoRefs.current.delete(anim.id); }}
                                key={anim.animation_file}
                                src={animationFileUrl(anim.animation_file)}
                                playsInline muted preload="metadata"
                                onEnded={() => setPlayingAnimId(null)}
                                style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none', opacity: 0.65 }} />
                            ) : anim.animation_type === 'text' || anim.animation_type === 'text_dynamic' ? (
                              <div style={{ width: '100%', height: '100%', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ color: anim.text_color || '#fff', fontSize: Math.min(pos.w * 0.25, 14), fontFamily: anim.text_font || 'sans-serif', textAlign: 'center', padding: 4, pointerEvents: 'none', overflow: 'hidden', wordBreak: 'break-word', opacity: 0.8 }}>
                                  {anim.animation_type === 'text_dynamic' ? '⌨️ Texte dynamique' : (anim.text_content || '📝')}
                                </span>
                              </div>
                            ) : (anim.animation_type === 'queue' || anim.animation_type === 'raffle') ? (
                              <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', gap: '4%', justifyContent: 'center', pointerEvents: 'none', boxSizing: 'border-box',
                                padding: anim.queue_bg_image ? '8% 10%' : '6%',
                                fontFamily: anim.text_font || 'sans-serif',
                                background: anim.queue_bg_image ? `url(/api/animation-file/${anim.queue_bg_image}) center/100% 100% no-repeat` : `${color}18` }}>
                                {anim.queue_show_counter !== 0 && anim.queue_show_counter !== false && (
                                  <div style={{ fontSize: Math.min(pos.w * 0.13, 11), fontWeight: 800, color: anim.text_color || '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}>💬 File d'attente · 3</div>
                                )}
                                {['Marie 🌸', 'Kevin', 'Sofia'].map((nm, i) => (
                                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4%', background: 'rgba(0,0,0,0.28)', borderRadius: 99, padding: '2% 6%' }}>
                                    <span style={{ fontWeight: 800, color: anim.text_color || '#fff', fontSize: Math.min(pos.w * 0.12, 10), opacity: 0.7 }}>{i + 1}</span>
                                    {(anim.queue_show_avatar !== 0 && anim.queue_show_avatar !== false) && <span style={{ width: '1.5em', height: '1.5em', borderRadius: '50%', background: 'rgba(255,255,255,0.35)', flex: 'none' }} />}
                                    <span style={{ color: anim.text_color || '#fff', fontWeight: 600, fontSize: Math.min(pos.w * 0.12, 11), textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>{nm}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ width: '100%', height: '100%', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <span style={{ fontSize: Math.min(pos.w * 0.4, 22), opacity: 0.7 }}>{icon}</span>
                              </div>
                            )}
                            {/* Uniquement le badge taille + ref en bas à droite */}
                            <div style={{ position: 'absolute', bottom: 0, right: 0, background: 'rgba(0,0,0,0.55)', padding: '1px 3px', pointerEvents: 'none' }}>
                              <span style={{ color, fontSize: 7, fontWeight: 700 }}>{isRef && '⭐ '}{Math.round(pos.w)}×{Math.round(pos.h)}%</span>
                            </div>
                            {isAdmin && <div onMouseDown={e => startDragFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 3 }} />}
                            {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                              <div key={corner} onMouseDown={e => startDragFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                style={{ position: 'absolute', width: 14, height: 14, background: color, borderRadius: 3, zIndex: 4,
                                  cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                                  ...(corner === 'tl' ? { top: -7, left: -7 } : corner === 'tr' ? { top: -7, right: -7 } : corner === 'bl' ? { bottom: -7, left: -7 } : { bottom: -7, right: -7 })
                                }} />
                            ))}
                          </div>
                        );
                      })}

                      {/* Boîtes de cadeaux cochées */}
                      {Array.from(checkedGiftIds).map(id => {
                        const anim = animations.find(a => a.id === id);
                        if (!anim || !anim.show_gift_image) return null;
                        const boxKey = `gift-${id}`;
                        const pos = pendingPos.get(boxKey) ?? { x: anim.gift_img_x ?? 5, y: anim.gift_img_y ?? 5, w: anim.gift_img_w ?? 20, h: anim.gift_img_h ?? 20 };
                        return (
                          <div key={boxKey}
                            style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                              zIndex: 3, boxSizing: 'border-box', overflow: 'hidden',
                              border: '2px dashed #f59e0b', background: 'rgba(245,158,11,0.12)',
                              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ fontSize: Math.min(pos.w * 0.4, 22), pointerEvents: 'none' }}>🎁</div>
                            <div style={{ color: '#fbbf24', fontSize: Math.min(pos.w * 0.2, 9), fontWeight: 700, background: 'rgba(0,0,0,0.65)', borderRadius: 3, padding: '1px 3px', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pointerEvents: 'none' }}>{anim.name}</div>
                            <div style={{ position: 'absolute', bottom: 2, right: 3, fontSize: 8, color: '#f59e0b', fontWeight: 700, background: 'rgba(0,0,0,0.75)', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none' }}>{Math.round(pos.w)}×{Math.round(pos.h)}%</div>
                            {isAdmin && <div onMouseDown={e => startDragFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 4 }} />}
                            {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                              <div key={corner} onMouseDown={e => startDragFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                style={{ position: 'absolute', width: 14, height: 14, background: '#f59e0b', borderRadius: 3, zIndex: 5,
                                  cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                                  ...(corner === 'tl' ? { top: -7, left: -7 } : corner === 'tr' ? { top: -7, right: -7 } : corner === 'bl' ? { bottom: -7, left: -7 } : { bottom: -7, right: -7 })
                                }} />
                            ))}
                          </div>
                        );
                      })}

                      {/* Boîtes « Question » des files d'attente cochées */}
                      {Array.from(checkedAnimIds).map(id => {
                        const anim = animations.find(a => a.id === id);
                        if (!anim || (anim.animation_type !== 'queue' && anim.animation_type !== 'raffle')) return null;
                        const boxKey = `qq-${id}`;
                        const pos = pendingPos.get(boxKey) ?? { x: anim.queue_q_pos_x ?? 15, y: anim.queue_q_pos_y ?? 38, w: anim.queue_q_pos_w ?? 70, h: anim.queue_q_pos_h ?? 24 };
                        return (
                          <div key={boxKey}
                            style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                              zIndex: 4, boxSizing: 'border-box', overflow: 'hidden',
                              border: '2px dashed #a855f7',
                              background: anim.queue_q_bg_image ? `url(/api/animation-file/${anim.queue_q_bg_image}) center/100% 100% no-repeat` : (anim.queue_q_bg_color || 'rgba(168,85,247,0.14)'),
                              fontFamily: anim.text_font || 'sans-serif',
                              display: 'flex', flexDirection: 'column', gap: '5%', alignItems: 'center', justifyContent: 'center', padding: '5%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4%', pointerEvents: 'none' }}>
                              {(anim.queue_show_avatar !== 0 && anim.queue_show_avatar !== false) && <span style={{ width: '1.6em', height: '1.6em', borderRadius: '50%', background: 'rgba(255,255,255,0.35)' }} />}
                              <span style={{ color: anim.text_color || '#fff', fontWeight: 800, fontSize: Math.min(pos.w * 0.08, 14), textShadow: '0 1px 3px rgba(0,0,0,0.75)' }}>Marie 🌸</span>
                            </div>
                            <span style={{ fontSize: Math.min(pos.w * 0.085, 16), fontWeight: 800, color: anim.text_color || '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.75)', textAlign: 'center', pointerEvents: 'none', lineHeight: 1.3 }}>{anim.animation_type === 'raffle' ? 'Défilement des participants · 30s' : "Est-ce que je vais retrouver l'amour ?"}</span>
                            <div style={{ position: 'absolute', bottom: 2, right: 3, fontSize: 8, color: '#c084fc', fontWeight: 700, background: 'rgba(0,0,0,0.7)', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none' }}>❓ {Math.round(pos.w)}×{Math.round(pos.h)}%</div>
                            {isAdmin && <div onMouseDown={e => startDragFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 5 }} />}
                            {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                              <div key={corner} onMouseDown={e => startDragFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                style={{ position: 'absolute', width: 14, height: 14, background: '#a855f7', borderRadius: 3, zIndex: 6,
                                  cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                                  ...(corner === 'tl' ? { top: -7, left: -7 } : corner === 'tr' ? { top: -7, right: -7 } : corner === 'bl' ? { bottom: -7, left: -7 } : { bottom: -7, right: -7 })
                                }} />
                            ))}
                          </div>
                        );
                      })}

                      {/* Boîtes « Boutons » (contrôles) des files d'attente cochées */}
                      {Array.from(checkedAnimIds).map(id => {
                        const anim = animations.find(a => a.id === id);
                        if (!anim || (anim.animation_type !== 'queue' && !(anim.animation_type === 'raffle' && anim.queue_sound))) return null;
                        const boxKey = `qc-${id}`;
                        const pos = pendingPos.get(boxKey) ?? { x: anim.queue_ctrl_pos_x ?? 2, y: anim.queue_ctrl_pos_y ?? 89, w: anim.queue_ctrl_pos_w ?? 34, h: anim.queue_ctrl_pos_h ?? 8 };
                        return (
                          <div key={boxKey}
                            style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                              zIndex: 5, boxSizing: 'border-box', border: '2px dashed #22c55e',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6%' }}>
                            <div style={{ flex: 1, height: '100%', background: 'linear-gradient(135deg,#16a34a,#15803d)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: Math.min(pos.h * 1.2, 18), pointerEvents: 'none' }}>▶</div>
                            <div style={{ flex: 1, height: '100%', background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: Math.min(pos.h * 1.2, 18), pointerEvents: 'none' }}>⏭</div>
                            <div style={{ position: 'absolute', bottom: -16, left: 0, fontSize: 8, color: '#22c55e', fontWeight: 700, background: 'rgba(0,0,0,0.7)', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none', whiteSpace: 'nowrap' }}>🎛 Boutons {Math.round(pos.w)}×{Math.round(pos.h)}%</div>
                            {isAdmin && <div onMouseDown={e => startDragFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 6 }} />}
                            {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                              <div key={corner} onMouseDown={e => startDragFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                style={{ position: 'absolute', width: 14, height: 14, background: '#22c55e', borderRadius: 3, zIndex: 7,
                                  cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                                  ...(corner === 'tl' ? { top: -7, left: -7 } : corner === 'tr' ? { top: -7, right: -7 } : corner === 'bl' ? { bottom: -7, left: -7 } : { bottom: -7, right: -7 })
                                }} />
                            ))}
                          </div>
                        );
                      })}

                      {/* Boîtes image cadeau / cadeau prioritaire des files cochées */}
                      {Array.from(checkedAnimIds).flatMap(id => {
                        const anim = animations.find(a => a.id === id);
                        if (!anim || (anim.animation_type !== 'queue' && anim.animation_type !== 'raffle')) return [];
                        const out: React.ReactNode[] = [];
                        const mk = (boxKey: string, show: any, def: { x: number; y: number; w: number; h: number }, col: string, label: string) => {
                          if (!show) return;
                          const pos = pendingPos.get(boxKey) ?? def;
                          out.push(
                            <div key={boxKey}
                              style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`, width: `${pos.w}%`, height: `${pos.h}%`,
                                zIndex: 5, boxSizing: 'border-box', border: `2px dashed ${col}`, background: `${col}1f`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: Math.min(pos.w * 0.5, 26), pointerEvents: 'none' }}>{label}</span>
                              <div style={{ position: 'absolute', bottom: -16, left: 0, fontSize: 8, color: col, fontWeight: 700, background: 'rgba(0,0,0,0.7)', borderRadius: 3, padding: '1px 3px', pointerEvents: 'none', whiteSpace: 'nowrap' }}>{label} {Math.round(pos.w)}×{Math.round(pos.h)}%</div>
                              {isAdmin && <div onMouseDown={e => startDragFullscreen(e, 'move', boxKey, pos)} style={{ position: 'absolute', inset: 0, cursor: 'move', zIndex: 6 }} />}
                              {isAdmin && (['tl','tr','bl','br'] as const).map(corner => (
                                <div key={corner} onMouseDown={e => startDragFullscreen(e, `resize-${corner}` as DragState['mode'], boxKey, pos)}
                                  style={{ position: 'absolute', width: 14, height: 14, background: col, borderRadius: 3, zIndex: 7,
                                    cursor: corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize',
                                    ...(corner === 'tl' ? { top: -7, left: -7 } : corner === 'tr' ? { top: -7, right: -7 } : corner === 'bl' ? { bottom: -7, left: -7 } : { bottom: -7, right: -7 })
                                  }} />
                              ))}
                            </div>
                          );
                        };
                        mk(`qg-${id}`, anim.queue_show_gift, { x: anim.queue_gift_pos_x ?? 70, y: anim.queue_gift_pos_y ?? 5, w: anim.queue_gift_pos_w ?? 18, h: anim.queue_gift_pos_h ?? 10 }, '#f59e0b', '🎁');
                        mk(`qpg-${id}`, anim.animation_type === 'raffle' ? !!anim.animation_file : anim.queue_show_pgift, { x: anim.queue_pgift_pos_x ?? 18, y: anim.queue_pgift_pos_y ?? 32, w: anim.queue_pgift_pos_w ?? 64, h: anim.queue_pgift_pos_h ?? 36 }, '#a855f7', anim.animation_type === 'raffle' ? '?? M�dia gagnant' : '?');
                        return out;
                      })}

                      {/* Message vide */}
                      {checkedAnimIds.size === 0 && checkedGiftIds.size === 0 && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                          <span style={{ color: '#334155', fontSize: 13, textAlign: 'center', lineHeight: 2 }}>← Coche des animations<br/>dans le panneau gauche</span>
                        </div>
                      )}

                      {/* Indicateur modifications en attente */}
                      {pendingDirty && (
                        <div style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(0,0,0,0.65)', borderRadius: 5, padding: '3px 8px', zIndex: 10, color: '#f59e0b', fontSize: 10, fontFamily: 'monospace', pointerEvents: 'none' }}>
                          ● Modifications non sauvegardées
                        </div>
                      )}

                      {/* ── Repère « zone sûre » TikTok LIVE ── */}
                      {showSafeZone && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none' }}>
                          {/* Bande haute : pseudo, spectateurs, classement cadeaux */}
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '12%', background: 'rgba(239,68,68,0.22)', borderBottom: '1px dashed rgba(239,68,68,0.7)' }}>
                            <span style={{ position: 'absolute', top: 3, left: 6, color: '#fecaca', fontSize: 9, fontWeight: 700 }}>Interface TikTok (pseudo, classement)</span>
                          </div>
                          {/* Bande basse : commentaires + barre de cadeaux */}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '24%', background: 'rgba(239,68,68,0.22)', borderTop: '1px dashed rgba(239,68,68,0.7)' }}>
                            <span style={{ position: 'absolute', bottom: 3, left: 6, color: '#fecaca', fontSize: 9, fontWeight: 700 }}>Commentaires & barre cadeaux</span>
                          </div>
                          {/* Marges latérales rognées sur téléphones étroits (Z Flip…) */}
                          <div style={{ position: 'absolute', top: '12%', bottom: '24%', left: 0, width: '6%', background: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.20) 0 6px, rgba(245,158,11,0.05) 6px 12px)', borderRight: '1px dashed rgba(245,158,11,0.7)' }} />
                          <div style={{ position: 'absolute', top: '12%', bottom: '24%', right: 0, width: '6%', background: 'repeating-linear-gradient(45deg, rgba(245,158,11,0.20) 0 6px, rgba(245,158,11,0.05) 6px 12px)', borderLeft: '1px dashed rgba(245,158,11,0.7)' }} />
                          {/* Zone sûre centrale */}
                          <div style={{ position: 'absolute', top: '12%', bottom: '24%', left: '6%', right: '6%', border: '2px dashed rgba(34,197,94,0.85)', borderRadius: 4 }}>
                            <span style={{ position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)', color: '#86efac', fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.5)', borderRadius: 3, padding: '1px 6px', whiteSpace: 'nowrap' }}>✅ Zone sûre — visible sur tous les téléphones</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Panneau de droite : outils ── */}
                  <div style={{ width: 180, flexShrink: 0, background: '#080d14', borderLeft: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 10px', overflowY: 'auto' }}>
                    {sectionTitle('Aligner')}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
                      {([
                        ['← G', 'Aligner à gauche',        (p: {x:number,y:number,w:number,h:number}) => ({ ...p, x: 0 })],
                        ['⊕ H', 'Centrer horizontalement', (p: {x:number,y:number,w:number,h:number}) => ({ ...p, x: (100 - p.w) / 2 })],
                        ['D →', 'Aligner à droite',        (p: {x:number,y:number,w:number,h:number}) => ({ ...p, x: 100 - p.w })],
                        ['↑ H', 'Aligner en haut',         (p: {x:number,y:number,w:number,h:number}) => ({ ...p, y: 0 })],
                        ['⊕ V', 'Centrer verticalement',   (p: {x:number,y:number,w:number,h:number}) => ({ ...p, y: (100 - p.h) / 2 })],
                        ['↓ B', 'Aligner en bas',          (p: {x:number,y:number,w:number,h:number}) => ({ ...p, y: 100 - p.h })],
                      ] as [string, string, (p: {x:number,y:number,w:number,h:number}) => {x:number,y:number,w:number,h:number}][]).map(([label, title, fn]) => (
                        <button key={title} title={title} disabled={checkedKeys.length === 0}
                          onClick={() => bulkAlignPending(fn, checkedKeys)}
                          style={{ padding: '6px 4px', borderRadius: 5, fontSize: 11, cursor: checkedKeys.length === 0 ? 'not-allowed' : 'pointer',
                            border: '1px solid #1e293b', background: '#111827', color: '#94a3b8', textAlign: 'center', opacity: checkedKeys.length === 0 ? 0.4 : 1 }}>
                          {label}
                        </button>
                      ))}
                    </div>

                    {sectionTitle('Disposer')}
                    <button disabled={checkedKeys.length < 2} onClick={() => bulkArrangePending('row', checkedKeys)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 11, cursor: checkedKeys.length < 2 ? 'not-allowed' : 'pointer',
                        border: '1px solid #1e293b', background: '#111827', color: '#94a3b8', textAlign: 'left', opacity: checkedKeys.length < 2 ? 0.4 : 1 }}>
                      ▤ Côte à côte
                    </button>
                    <button disabled={checkedKeys.length < 2} onClick={() => bulkArrangePending('col', checkedKeys)}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 11, cursor: checkedKeys.length < 2 ? 'not-allowed' : 'pointer',
                        border: '1px solid #1e293b', background: '#111827', color: '#94a3b8', textAlign: 'left', opacity: checkedKeys.length < 2 ? 0.4 : 1 }}>
                      ▥ En colonne
                    </button>

                    {sectionTitle('Taille & Position')}
                    <button disabled={!refPos || checkedKeys.length === 0}
                      onClick={() => { if (!refPos) return; bulkAlignPending(p => ({ ...p, w: refPos.w, h: refPos.h }), checkedKeys.filter(k => k !== refKey)); }}
                      title={refPos ? `Appliquer ${Math.round(refPos.w)}×${Math.round(refPos.h)}% à la sélection` : 'Sélectionne une référence ci-dessus (⭐)'}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 11, cursor: !refPos || checkedKeys.length === 0 ? 'not-allowed' : 'pointer',
                        border: `1px solid ${refPos ? '#2563eb' : '#1e293b'}`, background: refPos ? '#172554' : '#111827',
                        color: refPos ? '#93c5fd' : '#94a3b8', textAlign: 'left', opacity: !refPos || checkedKeys.length === 0 ? 0.4 : 1 }}>
                      {refPos ? `⊞ Même taille (${Math.round(refPos.w)}×${Math.round(refPos.h)}%)` : '⊞ Même taille'}
                    </button>
                    <button disabled={!refPos || checkedKeys.length === 0}
                      onClick={() => { if (!refPos) return; bulkAlignPending(p => ({ ...p, x: refPos.x, y: refPos.y }), checkedKeys.filter(k => k !== refKey)); }}
                      title={refPos ? `Appliquer x:${Math.round(refPos.x)}% y:${Math.round(refPos.y)}% à la sélection` : 'Sélectionne une référence ci-dessus (⭐)'}
                      style={{ width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 11, cursor: !refPos || checkedKeys.length === 0 ? 'not-allowed' : 'pointer',
                        border: `1px solid ${refPos ? '#2563eb' : '#1e293b'}`, background: refPos ? '#172554' : '#111827',
                        color: refPos ? '#93c5fd' : '#94a3b8', textAlign: 'left', opacity: !refPos || checkedKeys.length === 0 ? 0.4 : 1 }}>
                      {refPos ? `⊡ Même place (${Math.round(refPos.x)}, ${Math.round(refPos.y)}%)` : '⊡ Même place'}
                    </button>

                    <div style={{ flex: 1 }} />

                    <div style={{ borderTop: '1px solid #1e293b', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button onClick={() => setShowSafeZone(v => !v)}
                        title="Affiche les zones masquées par l'interface TikTok et la zone toujours visible quel que soit le téléphone"
                        style={{ width: '100%', padding: '7px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                          border: `1px solid ${showSafeZone ? '#16a34a' : '#1e293b'}`,
                          background: showSafeZone ? '#052e16' : '#111827',
                          color: showSafeZone ? '#86efac' : '#64748b', fontWeight: showSafeZone ? 700 : 400 }}>
                        {showSafeZone ? '✅ Zone sûre affichée' : '🛡️ Afficher la zone sûre'}
                      </button>
                      <button onClick={saveAllPending} disabled={!pendingDirty || savingPending || !isAdmin}
                        style={{ width: '100%', padding: '9px 8px', borderRadius: 7, fontSize: 12, fontWeight: 700,
                          cursor: !pendingDirty || savingPending || !isAdmin ? 'not-allowed' : 'pointer',
                          border: `1px solid ${pendingDirty && isAdmin ? '#854d0e' : '#1e293b'}`,
                          background: pendingDirty && isAdmin ? '#1c1007' : '#111827',
                          color: pendingDirty && isAdmin ? '#fbbf24' : '#334155',
                          opacity: !pendingDirty || !isAdmin ? 0.5 : 1 }}>
                        {savingPending ? '⏳ Sauvegarde…' : `💾 Sauvegarder (${saveCount})`}
                      </button>
                      <button onClick={() => setIsFullscreen(false)}
                        style={{ width: '100%', padding: '7px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                          border: '1px solid #1e293b', background: '#111827', color: '#64748b' }}>
                        ✕ Fermer
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Overlay */}
            {isAdmin && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '12px 14px', flexShrink: 0 }}>
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Overlay PNG</div>
                {selected.overlay_file ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ color: '#16a34a', fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span>✓</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.overlay_file}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <label style={{ flex: 1, textAlign: 'center', padding: '6px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#64748b', fontSize: 11, cursor: 'pointer', background: '#f8fafc' }}>
                        Changer
                        <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
                      </label>
                      <button onClick={handleRemoveOverlay} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, cursor: 'pointer' }}>
                        Supprimer
                      </button>
                    </div>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 8, border: '1px dashed #cbd5e1', color: '#94a3b8', fontSize: 12, cursor: 'pointer', background: '#f8fafc' }}>
                    ↑ {overlayUploading ? 'Upload…' : 'Choisir un overlay PNG'}
                    <input ref={overlayInputRef} type="file" accept=".png" style={{ display: 'none' }} onChange={handleOverlayUpload} disabled={overlayUploading} />
                  </label>
                )}
                {overlayMsg && <div style={{ marginTop: 6, fontSize: 11, color: overlayMsg.includes('!') || overlayMsg.includes('supprimé') ? '#16a34a' : '#dc2626' }}>{overlayMsg}</div>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 52 }}>Theme</div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>S&eacute;lectionnez un th&egrave;me</div>
          <div style={{ fontSize: 13 }}>ou cr&eacute;ez-en un nouveau</div>
        </div>
      )}

      {/* ── Modals ── */}
      {showThemeForm && (
        <Modal title={editingTheme ? 'Modifier le thème' : 'Nouveau thème'} onClose={() => setShowThemeForm(false)}>
          <LightField label="Nom du thème">
            <input style={inputLight} value={themeForm.name} placeholder="ex: Haribo"
              onChange={e => { const name = e.target.value; setThemeForm(f => ({ ...f, name, slug: editingTheme ? f.slug : slugify(name) })); }} />
          </LightField>
          {!editingTheme && (
            <LightField label="Identifiant unique (slug)">
              <input style={inputLight} value={themeForm.slug} placeholder="ex: haribo"
                onChange={e => setThemeForm(f => ({ ...f, slug: e.target.value }))} />
            </LightField>
          )}
          <LightField label="Illustration">
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...inputLight, flex: 1 }} value={themeForm.image_url} placeholder="URL d'une image (optionnel)"
                onChange={e => { setThemeForm(f => ({ ...f, image_url: e.target.value })); setImagePreview(e.target.value); setImageFile(null); }} />
              <button onClick={() => imageRef.current?.click()} style={btnSecondary}>📁 Upload</button>
            </div>
            <input ref={imageRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (!f) return; setImageFile(f); setThemeForm(tf => ({ ...tf, image_url: '' })); const reader = new FileReader(); reader.onload = ev => setImagePreview(ev.target?.result as string); reader.readAsDataURL(f); e.target.value = ''; }} />
          </LightField>
          {imagePreview && <div style={{ display: 'flex', justifyContent: 'center' }}><img src={imagePreview} alt="preview" style={{ width: 60, height: 90, objectFit: 'cover', borderRadius: 7, border: '1px solid #e2e8f0' }} onError={e => (e.target as HTMLImageElement).style.display = 'none'} /></div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnGhost} onClick={() => setShowThemeForm(false)}>Annuler</button>
            <button style={btnPrimary} onClick={saveTheme}>{editingTheme ? 'Enregistrer' : 'Créer'}</button>
          </div>
        </Modal>
      )}

      {showAnimForm && (
        <Modal title={editingAnim ? "Modifier l'animation" : 'Nouvelle animation'} onClose={() => setShowAnimForm(false)}>
          {animForm.animation_type !== 'text_dynamic' && (
            <LightField label="Nom (label affiché)">
              <input style={inputLight} value={animForm.name} placeholder="ex: Rose, Explosion…"
                onChange={e => setAnimForm(f => ({ ...f, name: e.target.value }))} />
            </LightField>
          )}
          {animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && (
            <LightField label="Type d'animation">
              <select style={inputLight} value={animForm.animation_type}
                onChange={e => setAnimForm(f => ({ ...f, animation_type: e.target.value as AnimFormState['animation_type'] }))}>
                <option value="video">🎬 Vidéo</option>
                <option value="audio">🔊 Son (audio)</option>
                <option value="text">📝 Texte (statique)</option>
                <option value="text_dynamic">⌨️ Texte dynamique (saisi en live)</option>
              </select>
            </LightField>
          )}
          {animForm.animation_type !== 'text_dynamic' && animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && (
            <LightField label="Déclenchement">
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                  padding: '8px 12px', borderRadius: 8, border: `1px solid ${animForm.trigger_type === 'auto' ? '#2563eb' : '#e2e8f0'}`,
                  background: animForm.trigger_type === 'auto' ? '#eff6ff' : '#f8fafc' }}>
                  <input type="radio" checked={animForm.trigger_type === 'auto'} onChange={() => setAnimForm(f => ({ ...f, trigger_type: 'auto' }))} />
                  🎁 Automatique (cadeau)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                  padding: '8px 12px', borderRadius: 8, border: `1px solid ${animForm.trigger_type === 'manual' ? '#7c3aed' : '#e2e8f0'}`,
                  background: animForm.trigger_type === 'manual' ? '#f5f3ff' : '#f8fafc' }}>
                  <input type="radio" checked={animForm.trigger_type === 'manual'} onChange={() => setAnimForm(f => ({ ...f, trigger_type: 'manual' }))} />
                  🎛️ Manuel (table de mixage)
                </label>
              </div>
            </LightField>
          )}
          {animForm.animation_type !== 'text_dynamic' && animForm.animation_type !== 'gift_grid' && animForm.animation_type !== 'queue' && animForm.animation_type !== 'raffle' && animForm.animation_type !== 'mobile_comments' && animForm.animation_type !== 'banner' && animForm.trigger_type === 'auto' && (
            <LightField label="">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${animForm.show_gift_image ? '#f59e0b' : '#e2e8f0'}`,
                background: animForm.show_gift_image ? '#fffbeb' : '#f8fafc' }}>
                <input type="checkbox" checked={animForm.show_gift_image}
                  onChange={e => setAnimForm(f => ({ ...f, show_gift_image: e.target.checked }))} />
                🎁 Afficher l'image du cadeau sur l'overlay
              </label>
              {animForm.show_gift_image && (
                <div style={{ color: '#92400e', fontSize: 11, marginTop: 4, padding: '4px 8px', background: '#fef3c7', borderRadius: 6, border: '1px solid #fde68a' }}>
                  Positionnez le cadre 🎁 dans la prévisualisation après avoir enregistré.
                </div>
              )}
            </LightField>
          )}
          {animForm.animation_type === 'gift_grid' && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>🎁 Zone Cadeaux Games</div>
              <div style={{ fontSize: 11, color: '#78350f', background: '#fef3c7', borderRadius: 6, padding: '6px 8px' }}>
                Affiche automatiquement tous les cadeaux assignés aux actions du jeu actif du streamer (via l'onglet Live interactif).
              </div>
              <LightField label="Nom de l'action — position">
                <select style={inputLight} value={animForm.gift_label_pos}
                  onChange={e => setAnimForm(f => ({ ...f, gift_label_pos: e.target.value as AnimFormState['gift_label_pos'] }))}>
                  <option value="none">Aucun</option>
                  <option value="above">Au-dessus de l'image</option>
                  <option value="below">En-dessous de l'image</option>
                  <option value="left">À gauche de l'image</option>
                  <option value="right">À droite de l'image</option>
                  <option value="center">Centré sur l'image</option>
                </select>
              </LightField>
              {animForm.gift_label_pos !== 'none' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <LightField label="Couleur du texte" style={{ flex: 1 }}>
                    <input type="color" value={animForm.gift_label_color}
                      onChange={e => setAnimForm(f => ({ ...f, gift_label_color: e.target.value }))}
                      style={{ width: '100%', height: 32, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                  </LightField>
                  <LightField label="Taille (px)" style={{ flex: 1 }}>
                    <input type="number" style={inputLight} min={8} max={48} value={animForm.gift_label_size}
                      onChange={e => setAnimForm(f => ({ ...f, gift_label_size: Number(e.target.value) }))} />
                  </LightField>
                </div>
              )}
              <LightField label="Colonnes (0 = automatique)">
                <input type="number" style={inputLight} min={0} max={10} value={animForm.gift_cols}
                  onChange={e => setAnimForm(f => ({ ...f, gift_cols: Number(e.target.value) }))} />
              </LightField>
              <div style={{ color: '#92400e', fontSize: 11 }}>
                💡 La position et la taille de la zone se règlent dans la prévisualisation.
              </div>
            </div>
          )}
          {animForm.animation_type === 'queue' ? (
            <QueueFields form={animForm} set={setAnimForm} files={animFiles} onUpload={uploadQueueAsset} />
          ) : animForm.animation_type === 'mobile_comments' ? (
            <MobileCommentsFields form={animForm} set={setAnimForm} />
          ) : animForm.animation_type === 'raffle' ? (
            <RaffleFields form={animForm} set={setAnimForm} files={animFiles} onUpload={uploadQueueAsset} />
          ) : animForm.animation_type === 'banner' ? (
            <BannerFields form={animForm} set={setAnimForm} />
          ) : (animForm.animation_type === 'video' || animForm.animation_type === 'audio') ? (
            <LightField label="Fichier animation">
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...inputLight, flex: 1 }} value={animForm.animation_file} onChange={e => setAnimForm(f => ({ ...f, animation_file: e.target.value }))}>
                  <option value="">— Choisir —</option>
                  {animFiles.filter(af => {
                    const ext = af.split('.').pop()?.toLowerCase() ?? '';
                    if (animForm.animation_type === 'video') return ['mp4','webm','mov'].includes(ext);
                    if (animForm.animation_type === 'audio') return ['mp3','ogg','wav','aac'].includes(ext);
                    return true;
                  }).map(af => <option key={af} value={af}>{af}</option>)}
                </select>
                <input ref={animFileRef} type="file" accept=".mp4,.webm,.mov,.mp3,.ogg,.wav,.aac" style={{ display: 'none' }} onChange={handleUploadAnimFile} />
                <button onClick={() => animFileRef.current?.click()} style={btnSecondary}>{uploadingAnim ? '⏳' : '📁 Upload'}</button>
              </div>
            </LightField>
          ) : animForm.animation_type !== 'gift_grid' ? (
            <>
              {animForm.animation_type === 'text' && (
                <LightField label="Texte à afficher">
                  <textarea style={{ ...inputLight, minHeight: 60, resize: 'vertical' }} value={animForm.text_content}
                    placeholder="ex: Bienvenue ! 🎉"
                    onChange={e => setAnimForm(f => ({ ...f, text_content: e.target.value }))} />
                </LightField>
              )}
              {animForm.animation_type === 'text_dynamic' && (
                <div style={{ color: '#64748b', fontSize: 12, padding: '6px 0', background: '#f8fafc', borderRadius: 6, paddingLeft: 10, borderLeft: '3px solid #e2e8f0' }}>ℹ️ Le streamer saisit le texte en direct depuis son overlay mobile.</div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <LightField label="Taille (px)" style={{ flex: 1 }}>
                  <input type="number" style={inputLight} value={animForm.text_size} min={12} max={200}
                    onChange={e => setAnimForm(f => ({ ...f, text_size: Number(e.target.value) }))} />
                </LightField>
                <LightField label="Couleur" style={{ flex: 2 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="color" value={animForm.text_color}
                      onChange={e => setAnimForm(f => ({ ...f, text_color: e.target.value }))}
                      style={{ width: 36, height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
                    <input style={{ ...inputLight, flex: 1 }} value={animForm.text_color}
                      onChange={e => setAnimForm(f => ({ ...f, text_color: e.target.value }))} />
                  </div>
                </LightField>
              </div>
              <LightField label="Police">
                <select style={inputLight} value={animForm.text_font}
                  onChange={e => setAnimForm(f => ({ ...f, text_font: e.target.value }))}>
                  <option value="sans-serif">Sans-serif</option>
                  <option value="serif">Serif</option>
                  <option value="Impact">Impact</option>
                  <option value="Arial Black">Arial Black</option>
                  <option value="Courier New">Courier New</option>
                  <option value="Comic Sans MS">Comic Sans MS</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Trebuchet MS">Trebuchet MS</option>
                </select>
              </LightField>
              <div style={{ display: 'flex', gap: 8 }}>
                <LightField label="Effet" style={{ flex: 2 }}>
                  <select style={inputLight} value={animForm.text_effect}
                    onChange={e => setAnimForm(f => ({ ...f, text_effect: e.target.value }))}>
                    <option value="fade">Fondu</option>
                    <option value="scroll-left">Défilement gauche</option>
                    <option value="scroll-right">Défilement droite</option>
                    <option value="scroll-up">Défilement haut</option>
                    <option value="scroll-down">Défilement bas</option>
                    <option value="typewriter">Machine à écrire</option>
                    <option value="bounce">Rebond</option>
                    <option value="zoom">Zoom</option>
                  </select>
                </LightField>
                <LightField label="Boucles" style={{ flex: 1 }}>
                  <input type="number" style={inputLight} value={animForm.text_loops} min={1} max={20}
                    onChange={e => setAnimForm(f => ({ ...f, text_loops: Number(e.target.value) }))} />
                </LightField>
                <LightField label="Durée/boucle (s)" style={{ flex: 1 }}>
                  <input type="number" style={inputLight} value={animForm.text_duration} min={1} max={60} step={0.5}
                    onChange={e => setAnimForm(f => ({ ...f, text_duration: Number(e.target.value) }))} />
                </LightField>
              </div>
              {/* Fond de la zone texte */}
              <LightField label="Fond de la zone">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={animForm.text_bg_color}
                    onChange={e => setAnimForm(f => ({ ...f, text_bg_color: e.target.value }))}
                    style={{ width: 36, height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }} />
                  <input type="range" min={0} max={100} value={animForm.text_bg_opacity}
                    onChange={e => setAnimForm(f => ({ ...f, text_bg_opacity: Number(e.target.value) }))}
                    style={{ flex: 1 }} />
                  <span style={{ color: '#64748b', fontSize: 12, width: 36, textAlign: 'right', flexShrink: 0 }}>{animForm.text_bg_opacity}%</span>
                </div>
              </LightField>
              {animForm.text_bg_opacity > 0 && (
                <LightField label="Apparition du fond">
                  <select style={inputLight} value={animForm.text_bg_anim}
                    onChange={e => setAnimForm(f => ({ ...f, text_bg_anim: e.target.value }))}>
                    <option value="none">Instantané</option>
                    <option value="pivot-h">Pivot horizontal (gauche→droite)</option>
                    <option value="pivot-h-r">Pivot horizontal (droite→gauche)</option>
                    <option value="pivot-v">Pivot vertical (haut→bas)</option>
                    <option value="pivot-v-b">Pivot vertical (bas→haut)</option>
                    <option value="expand">Expansion horizontale (centre)</option>
                    <option value="expand-v">Expansion verticale (centre)</option>
                    <option value="fade">Fondu</option>
                    <option value="slide-l">Glissement depuis la gauche</option>
                    <option value="slide-r">Glissement depuis la droite</option>
                    <option value="slide-t">Glissement depuis le haut</option>
                    <option value="slide-b">Glissement depuis le bas</option>
                  </select>
                </LightField>
              )}
            </>
          ) : null}
          <div style={{ color: '#94a3b8', fontSize: 12 }}>La position se règle dans la prévisualisation.</div>
          {animError && <div style={{ color: '#dc2626', fontSize: 12, background: '#fef2f2', borderRadius: 6, padding: '7px 10px', border: '1px solid #fecaca' }}>{animError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnGhost} onClick={() => setShowAnimForm(false)}>Annuler</button>
            <button style={btnPrimary} onClick={saveAnim}>{editingAnim ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </Modal>
      )}

      {showStaticForm && (
        <Modal title={editingStaticId !== null ? "Modifier l'image permanente" : 'Nouvelle image permanente'} onClose={() => setShowStaticForm(false)}>
          <LightField label="Nom">
            <input style={inputLight} value={staticForm.name} placeholder="ex: Logo, Cadre…"
              onChange={e => setStaticForm(f => ({ ...f, name: e.target.value }))} />
          </LightField>
          <LightField label="Fichier image">
            <select style={inputLight} value={staticForm.image_file}
              onChange={e => setStaticForm(f => ({ ...f, image_file: e.target.value }))}>
              <option value="">— Choisir —</option>
              {animFiles.filter(af => {
                const ext = af.split('.').pop()?.toLowerCase() ?? '';
                return ['png','jpg','jpeg','gif','webp','svg'].includes(ext);
              }).map(af => <option key={af} value={af}>{af}</option>)}
            </select>
          </LightField>
          {staticForm.image_file && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={`/api/animation-files/${staticForm.image_file}`} alt="preview"
                style={{ maxHeight: 100, maxWidth: '100%', borderRadius: 8, border: '1px solid #e2e8f0', objectFit: 'contain' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          )}
          <LightField label="Couche (layer)">
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                padding: '8px 12px', borderRadius: 8, border: `1px solid ${staticForm.layer === 'above' ? '#16a34a' : '#e2e8f0'}`,
                background: staticForm.layer === 'above' ? '#f0fdf4' : '#f8fafc' }}>
                <input type="radio" checked={staticForm.layer === 'above'} onChange={() => setStaticForm(f => ({ ...f, layer: 'above' }))} />
                ↑ Dessus (visible par-dessus les animations)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flex: 1,
                padding: '8px 12px', borderRadius: 8, border: `1px solid ${staticForm.layer === 'below' ? '#ca8a04' : '#e2e8f0'}`,
                background: staticForm.layer === 'below' ? '#fefce8' : '#f8fafc' }}>
                <input type="radio" checked={staticForm.layer === 'below'} onChange={() => setStaticForm(f => ({ ...f, layer: 'below' }))} />
                ↓ Dessous (caché par les animations)
              </label>
            </div>
          </LightField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['pos_x','pos_y','pos_w','pos_h'] as const).map(k => (
              <LightField key={k} label={k === 'pos_x' ? 'Position X (%)' : k === 'pos_y' ? 'Position Y (%)' : k === 'pos_w' ? 'Largeur (%)' : 'Hauteur (%)'}>
                <input type="number" style={inputLight} value={staticForm[k]} min={0} max={100} step={0.5}
                  onChange={e => setStaticForm(f => ({ ...f, [k]: Number(e.target.value) }))} />
              </LightField>
            ))}
          </div>
          <LightField label="Ordre (z-index relatif)">
            <input type="number" style={inputLight} value={staticForm.sort_order} min={0} max={100}
              onChange={e => setStaticForm(f => ({ ...f, sort_order: Number(e.target.value) }))} />
          </LightField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <button style={btnGhost} onClick={() => setShowStaticForm(false)}>Annuler</button>
            <button style={btnPrimary} onClick={saveStaticElem}>{editingStaticId !== null ? 'Enregistrer' : 'Ajouter'}</button>
          </div>
        </Modal>
      )}

      <style>{`
        .theme-row:hover .row-actions { opacity: 1 !important; }
        .theme-row:hover { background: #f8fafc !important; }
      `}</style>
    </div>
  );
}

function MobileStreamersSection({ themeId, themeStreamers, isAdmin, onToggle, onExpand }: {
  themeId: number; themeStreamers: ThemeStreamer[];
  isAdmin: boolean; onToggle: (id: number, has: boolean) => void; onExpand: () => void;
}) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => { if (!open) onExpand(); setOpen(o => !o); };
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid #e2e8f0', paddingTop: 10 }}>
      <div onClick={handleOpen} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
        <span style={{ color: '#374151', fontSize: 12, fontWeight: 600 }}>Streamers</span>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {themeStreamers.map(s => {
            const has = s.has_theme === 1;
            return (
              <div key={s.id} style={{ background: '#fff', border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: has ? '#ede9fe' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: has ? '#7c3aed' : '#94a3b8', flexShrink: 0, overflow: 'hidden' }}>
                  {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : s.display_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name}</div>
                </div>
                {isAdmin && (
                  <button onClick={() => onToggle(s.id, has)}
                    style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${has ? '#bfdbfe' : '#e2e8f0'}`, background: has ? '#eff6ff' : '#f8fafc', color: has ? '#2563eb' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: has ? 600 : 400 }}>
                    {has ? '✓' : '+'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DetailTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: '12px 14px', background: 'none', border: 'none', borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`, color: active ? '#2563eb' : '#64748b', fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', transition: 'all 0.12s', marginBottom: -1 }}>
      {children}
    </button>
  );
}

function MediaPicker({ label, hint, accept, kind, value, files, onUpload, onChange }: {
  label: string; hint?: string; accept: string;
  kind: 'image' | 'audio' | 'media'; value: string; files: string[];
  onUpload: (file: File) => Promise<string | undefined>;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const exts = kind === 'image'
    ? ['png', 'jpg', 'jpeg', 'gif', 'webp']
    : kind === 'audio'
      ? ['mp3', 'ogg', 'wav', 'aac']
      : ['mp3', 'ogg', 'wav', 'aac', 'mp4', 'webm', 'mov'];
  const list = files.filter(f => exts.includes(f.split('.').pop()?.toLowerCase() ?? ''));
  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    const name = await onUpload(file);
    setBusy(false);
    if (name) onChange(name);
  };
  return (
    <LightField label={label}>
      {hint && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: -3, marginBottom: 2 }}>{hint}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <select style={{ ...inputLight, flex: 1 }} value={value} onChange={e => onChange(e.target.value)}>
          <option value="">— Aucun —</option>
          {list.map(af => {
            const dn = af.includes('/') ? af.split('/').pop()! : af;
            return <option key={af} value={af}>{dn}</option>;
          })}
        </select>
        <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={pick} />
        <button type="button" onClick={() => ref.current?.click()} style={btnSecondary}>{busy ? '⏳' : '📁'}</button>
      </div>
      {kind === 'image' && value && (
        <div style={{ marginTop: 6 }}>
          <img src={`/api/animation-file/${value}`} alt="" style={{ maxHeight: 70, maxWidth: '100%', borderRadius: 6, border: '1px solid #e2e8f0', objectFit: 'contain' }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
      )}
    </LightField>
  );
}


function MobileCommentsFields({ form, set }: {
  form: AnimFormState;
  set: React.Dispatch<React.SetStateAction<AnimFormState>>;
}) {
  return (
    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#0369a1' }}>Commentaires mobile</div>
      <div style={{ fontSize: 11, color: '#075985', background: '#e0f2fe', borderRadius: 6, padding: '6px 8px' }}>
        Affiche tous les commentaires TikTok dans une zone de l'overlay mobile, avec un d?filement vertical fa?on TikTok.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <LightField label="Commentaires visibles">
          <input type="number" min={1} max={10} style={inputLight} value={form.queue_max_visible}
            onChange={e => set(f => ({ ...f, queue_max_visible: Number(e.target.value) }))} />
        </LightField>
        <LightField label="Taille texte">
          <input type="number" min={12} max={80} style={inputLight} value={form.text_size}
            onChange={e => set(f => ({ ...f, text_size: Number(e.target.value) }))} />
        </LightField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <LightField label="Couleur pseudo/commentaire">
          <input type="color" value={form.text_color} onChange={e => set(f => ({ ...f, text_color: e.target.value }))}
            style={{ width: '100%', height: 34, border: '1px solid #e2e8f0', borderRadius: 6 }} />
        </LightField>
        <LightField label="Police">
          <select style={inputLight} value={form.text_font} onChange={e => set(f => ({ ...f, text_font: e.target.value }))}>
            <option value="sans-serif">Sans-serif</option>
            <option value="Arial Black">Arial Black</option>
            <option value="Impact">Impact</option>
            <option value="Verdana">Verdana</option>
            <option value="Georgia">Georgia</option>
          </select>
        </LightField>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <LightField label="Fond">
          <input type="color" value={form.text_bg_color} onChange={e => set(f => ({ ...f, text_bg_color: e.target.value }))}
            style={{ width: '100%', height: 34, border: '1px solid #e2e8f0', borderRadius: 6 }} />
        </LightField>
        <LightField label="Opacit? fond">
          <input type="number" min={0} max={100} style={inputLight} value={form.text_bg_opacity}
            onChange={e => set(f => ({ ...f, text_bg_opacity: Number(e.target.value) }))} />
        </LightField>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#075985', fontWeight: 700 }}>
        <input type="checkbox" checked={form.queue_show_avatar}
          onChange={e => set(f => ({ ...f, queue_show_avatar: e.target.checked }))} />
        Afficher les avatars TikTok
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <LightField label="Voix ElevenLabs - ID">
          <input style={inputLight} value={form.mobile_comment_voice_id} placeholder="voice_id"
            onChange={e => set(f => ({ ...f, mobile_comment_voice_id: e.target.value }))} />
        </LightField>
        <LightField label="Nom de la voix">
          <input style={inputLight} value={form.mobile_comment_voice_name} placeholder="ex: Sarah"
            onChange={e => set(f => ({ ...f, mobile_comment_voice_name: e.target.value }))} />
        </LightField>
      </div>
      <div style={{ color: '#075985', fontSize: 11 }}>
        La position et la taille de la zone se r?glent dans la pr?visualisation du th?me.
      </div>
    </div>
  );
}

function QueueFields({ form, set, files, onUpload }: {
  form: AnimFormState;
  set: React.Dispatch<React.SetStateAction<AnimFormState>>;
  files: string[];
  onUpload: (file: File) => Promise<string | undefined>;
}) {
  // Voix ElevenLabs (chargées si la clé est configurée)
  const [ttsVoices, setTtsVoices] = React.useState<{ voice_id: string; name: string }[]>([]);
  const [ttsErr, setTtsErr] = React.useState<string | null>(null);
  const [ttsTesting, setTtsTesting] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    getElevenLabsVoices().then(r => {
      if (cancelled) return;
      setTtsVoices(r.data.voices || []);
    }).catch((e: any) => {
      if (cancelled) return;
      setTtsErr(e?.response?.data?.detail || null);
    });
    return () => { cancelled = true; };
  }, []);
  const testTTS = async () => {
    if (!form.queue_tts_voice_id) return;
    setTtsTesting(true);
    try {
      const r = await ttsElevenLabs(form.queue_tts_voice_id,
        "Bonjour, ceci est un test de la voix utilisée pour lire les questions.");
      const url = URL.createObjectURL(r.data as Blob);
      const audio = new Audio(url);
      audio.play();
    } catch (e: any) {
      alert('Erreur TTS : ' + (e?.response?.data?.detail || ''));
    } finally {
      setTtsTesting(false);
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.45, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
        Affiche la liste des viewers qui ont offert le cadeau lié à cet élément (le cadeau se règle dans <b>Live interactif</b> du streamer) puis posé une question dans le chat. Passez à la question suivante avec les boutons de modération.
      </div>

      {/* ── Lecture vocale (TTS ElevenLabs) ── */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 14 }}>🔊</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>Lecture vocale des questions (ElevenLabs)</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <div onClick={() => set(f => ({ ...f, queue_tts_enabled: !f.queue_tts_enabled }))}
              style={{ width: 36, height: 20, borderRadius: 10, background: form.queue_tts_enabled ? '#16a34a' : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 2, left: form.queue_tts_enabled ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <span style={{ fontSize: 12, color: '#374151', fontWeight: form.queue_tts_enabled ? 700 : 500 }}>
              {form.queue_tts_enabled ? 'Activée' : 'Désactivée'}
            </span>
          </label>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
          Quand la file est active, chaque nouvelle question sera lue à voix haute par la voix choisie. Réglage par défaut de cette animation ; chaque streamer peut le surcharger.
        </div>
        {form.queue_tts_enabled && (
          <>
            {ttsErr && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '6px 8px', fontSize: 11, marginBottom: 6 }}>
                {ttsErr === 'Clé ElevenLabs non configurée'
                  ? <>Clé non configurée. Va dans <b>Paramètres → Applications → ElevenLabs</b>.</>
                  : ttsErr}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Voix</div>
                <select value={form.queue_tts_voice_id || ''}
                  onChange={e => set(f => ({ ...f, queue_tts_voice_id: e.target.value }))}
                  disabled={ttsVoices.length === 0}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, background: '#fff' }}>
                  <option value="">— choisir une voix —</option>
                  {ttsVoices.map(v => (
                    <option key={v.voice_id} value={v.voice_id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <button type="button" onClick={testTTS}
                disabled={!form.queue_tts_voice_id || ttsTesting}
                style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: form.queue_tts_voice_id ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap', opacity: form.queue_tts_voice_id ? 1 : 0.6 }}>
                {ttsTesting ? '🔊 …' : '🔊 Tester'}
              </button>
            </div>
          </>
        )}
      </div>

      <MediaPicker
        label="Fond de la liste (image, ex : parchemin)"
        hint="Image affichée derrière la liste des pseudos sur l'overlay"
        accept="image/*" kind="image" files={files}
        value={form.queue_bg_image}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_bg_image: v }))}
      />
      <MediaPicker
        label="Fond de la question affichée (image, optionnel)"
        hint="Cadre derrière la question quand vous l'affichez à l'écran"
        accept="image/*" kind="image" files={files}
        value={form.queue_q_bg_image}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_q_bg_image: v }))}
      />
      <LightField label="Couleur du fond de la question">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="color" value={form.queue_q_bg_color || '#111111'}
            onChange={e => set(f => ({ ...f, queue_q_bg_color: e.target.value }))}
            style={{ width: 42, height: 34, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
          <input style={{ ...inputLight, flex: 1 }} value={form.queue_q_bg_color || '#111111'}
            onChange={e => set(f => ({ ...f, queue_q_bg_color: e.target.value }))} />
        </div>
      </LightField>
      <MediaPicker
        label="Son à l'affichage d'une question (optionnel)"
        accept="audio/*" kind="audio" files={files}
        value={form.queue_sound}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_sound: v }))}
      />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.queue_show_avatar}
            onChange={e => set(f => ({ ...f, queue_show_avatar: e.target.checked }))} />
          Photos de profil
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.queue_show_counter}
            onChange={e => set(f => ({ ...f, queue_show_counter: e.target.checked }))} />
          Afficher le compteur
        </label>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#92400e', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.queue_show_gift}
            onChange={e => set(f => ({ ...f, queue_show_gift: e.target.checked }))} />
          🎁 Afficher l'image du cadeau
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#92400e', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.queue_show_pgift}
            onChange={e => set(f => ({ ...f, queue_show_pgift: e.target.checked }))} />
          ⭐ Afficher l'image du cadeau prioritaire
        </label>
        <div style={{ width: '100%', color: '#a16207', fontSize: 11 }}>
          Positionnez chaque image dans la prévisualisation plein écran (cadres 🎁 / ⭐).
        </div>
      </div>

      {form.queue_show_gift && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>🎁 Texte du cadeau</div>
          <LightField label="Texte (laisser vide = aucun)">
            <input style={inputLight} value={form.queue_gift_label} placeholder="ex: Poser une question"
              onChange={e => set(f => ({ ...f, queue_gift_label: e.target.value }))} />
          </LightField>
          <div style={{ display: 'flex', gap: 8 }}>
            <LightField label="Position" style={{ flex: 1 }}>
              <select style={inputLight} value={form.queue_gift_label_pos}
                onChange={e => set(f => ({ ...f, queue_gift_label_pos: e.target.value }))}>
                <option value="none">Masqué</option>
                <option value="above">Au-dessus</option>
                <option value="below">En dessous</option>
                <option value="left">À gauche</option>
                <option value="right">À droite</option>
              </select>
            </LightField>
            <LightField label="Taille (px)" style={{ flex: 1 }}>
              <input type="number" style={inputLight} min={8} max={80} value={form.queue_gift_label_size}
                onChange={e => set(f => ({ ...f, queue_gift_label_size: Number(e.target.value) }))} />
            </LightField>
            <LightField label="Couleur" style={{ flex: 1 }}>
              <input type="color" value={form.queue_gift_label_color}
                onChange={e => set(f => ({ ...f, queue_gift_label_color: e.target.value }))}
                style={{ width: '100%', height: 34, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
            </LightField>
          </div>
        </div>
      )}

      {form.queue_show_pgift && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#a16207' }}>⭐ Texte du cadeau prioritaire</div>
          <LightField label="Texte (laisser vide = aucun)">
            <input style={inputLight} value={form.queue_pgift_label} placeholder="ex: Question prioritaire"
              onChange={e => set(f => ({ ...f, queue_pgift_label: e.target.value }))} />
          </LightField>
          <div style={{ display: 'flex', gap: 8 }}>
            <LightField label="Position" style={{ flex: 1 }}>
              <select style={inputLight} value={form.queue_pgift_label_pos}
                onChange={e => set(f => ({ ...f, queue_pgift_label_pos: e.target.value }))}>
                <option value="none">Masqué</option>
                <option value="above">Au-dessus</option>
                <option value="below">En dessous</option>
                <option value="left">À gauche</option>
                <option value="right">À droite</option>
              </select>
            </LightField>
            <LightField label="Taille (px)" style={{ flex: 1 }}>
              <input type="number" style={inputLight} min={8} max={80} value={form.queue_pgift_label_size}
                onChange={e => set(f => ({ ...f, queue_pgift_label_size: Number(e.target.value) }))} />
            </LightField>
            <LightField label="Couleur" style={{ flex: 1 }}>
              <input type="color" value={form.queue_pgift_label_color}
                onChange={e => set(f => ({ ...f, queue_pgift_label_color: e.target.value }))}
                style={{ width: '100%', height: 34, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
            </LightField>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <LightField label="Pseudos visibles" style={{ flex: 1 }}>
          <input type="number" style={inputLight} min={1} max={20} value={form.queue_max_visible}
            onChange={e => set(f => ({ ...f, queue_max_visible: Number(e.target.value) }))} />
        </LightField>
        <LightField label="Particules" style={{ flex: 1 }}>
          <select style={inputLight} value={form.queue_particle}
            onChange={e => set(f => ({ ...f, queue_particle: e.target.value }))}>
            <option value="none">Aucune</option>
            <option value="confetti">Confettis</option>
            <option value="hearts">Cœurs</option>
            <option value="stars">Étoiles</option>
            <option value="sparkle">Étincelles</option>
          </select>
        </LightField>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <LightField label="Apparition" style={{ flex: 1 }}>
          <select style={inputLight} value={form.queue_appear_anim}
            onChange={e => set(f => ({ ...f, queue_appear_anim: e.target.value }))}>
            <option value="fade">Fondu</option>
            <option value="zoom">Zoom</option>
            <option value="slide">Glissement</option>
            <option value="flip">Flip</option>
          </select>
        </LightField>
        <LightField label="Disparition" style={{ flex: 1 }}>
          <select style={inputLight} value={form.queue_disappear_anim}
            onChange={e => set(f => ({ ...f, queue_disappear_anim: e.target.value }))}>
            <option value="fade">Fondu</option>
            <option value="zoom">Zoom</option>
            <option value="slide">Glissement</option>
            <option value="flip">Flip</option>
          </select>
        </LightField>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginTop: 2 }}>Texte (pseudos & question)</div>
      <div style={{ color: '#64748b', fontSize: 12, lineHeight: 1.45, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
        La taille du pseudo et de la question s'adapte automatiquement a la zone pour tenir sur 1, 2 ou 3 lignes selon la longueur.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <LightField label="Couleur" style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="color" value={form.text_color}
              onChange={e => set(f => ({ ...f, text_color: e.target.value }))}
              style={{ width: 36, height: 32, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
            <input style={{ ...inputLight, flex: 1 }} value={form.text_color}
              onChange={e => set(f => ({ ...f, text_color: e.target.value }))} />
          </div>
        </LightField>
      </div>
      <LightField label="Police">
        <select style={inputLight} value={form.text_font}
          onChange={e => set(f => ({ ...f, text_font: e.target.value }))}>
          <option value="sans-serif">Sans-serif</option>
          <option value="serif">Serif</option>
          <option value="Impact">Impact</option>
          <option value="Arial Black">Arial Black</option>
          <option value="Georgia">Georgia</option>
          <option value="Verdana">Verdana</option>
          <option value="Trebuchet MS">Trebuchet MS</option>
        </select>
      </LightField>
    </div>
  );
}

function RaffleFields({ form, set, files, onUpload }: {
  form: AnimFormState;
  set: React.Dispatch<React.SetStateAction<AnimFormState>>;
  files: string[];
  onUpload: (file: File) => Promise<string | undefined>;
}) {
  const audioVideo = files.filter(f => ['mp3','ogg','wav','aac','mp4','webm','mov'].includes((f.split('.').pop() || '').toLowerCase()));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ color: '#475569', fontSize: 12, lineHeight: 1.45, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
        Le cadeau de participation se choisit ensuite dans <b>Mes lives interactifs</b>. Les participants restent mémorisés tant que le tirage n'a pas été lancé.
      </div>
      <LightField label="Mode du concours">
        <select style={inputLight} value={form.text_effect}
          onChange={e => set(f => ({ ...f, text_effect: e.target.value }))}>
          <option value="gift_random">Tirage parmi les donateurs du cadeau</option>
          <option value="top_donor">Meilleurs donateurs</option>
        </select>
      </LightField>
      <div style={{ display: 'flex', gap: 8 }}>
        <LightField label="Lignes affichées" style={{ flex: 1 }}>
          <select style={inputLight} value={form.queue_max_visible}
            onChange={e => set(f => ({ ...f, queue_max_visible: Number(e.target.value) }))}>
            <option value={3}>3</option>
            <option value={5}>5</option>
            <option value={10}>10</option>
          </select>
        </LightField>
        <LightField label="Taille texte" style={{ flex: 1 }}>
          <input type="number" style={inputLight} min={12} max={120} value={form.text_size}
            onChange={e => set(f => ({ ...f, text_size: Number(e.target.value) }))} />
        </LightField>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <LightField label="Couleur" style={{ flex: 1 }}>
          <input type="color" value={form.text_color}
            onChange={e => set(f => ({ ...f, text_color: e.target.value }))}
            style={{ width: '100%', height: 34, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer' }} />
        </LightField>
        <LightField label="Police" style={{ flex: 2 }}>
          <select style={inputLight} value={form.text_font}
            onChange={e => set(f => ({ ...f, text_font: e.target.value }))}>
            <option value="sans-serif">Sans-serif</option>
            <option value="Impact">Impact</option>
            <option value="Arial Black">Arial Black</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
          </select>
        </LightField>
      </div>
      <MediaPicker
        label="Fond de la liste / classement"
        accept="image/*" kind="image" files={files}
        value={form.queue_bg_image}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_bg_image: v }))}
      />
      <MediaPicker
        label="Fond de la zone de tirage"
        accept="image/*" kind="image" files={files}
        value={form.queue_q_bg_image}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_q_bg_image: v }))}
      />
      <MediaPicker
        label="Animation ou son à chaque nouvelle inscription"
        hint="Audio ou vidéo. Si c'est une vidéo, positionnez le cadre « Nouvelle inscription » en plein écran."
        accept=".mp4,.webm,.mov,.mp3,.ogg,.wav,.aac" kind="media" files={files}
        value={form.queue_sound}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, queue_sound: v }))}
      />
      <MediaPicker
        label="Vidéo ou son du gagnant"
        hint="Audio ou vidéo. Si c'est une vidéo, positionnez le cadre « Média gagnant » en plein écran."
        accept=".mp4,.webm,.mov,.mp3,.ogg,.wav,.aac" kind="media" files={audioVideo}
        value={form.animation_file}
        onUpload={onUpload}
        onChange={v => set(f => ({ ...f, animation_file: v }))}
      />
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#92400e', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.queue_show_gift}
            onChange={e => set(f => ({ ...f, queue_show_gift: e.target.checked }))} />
          Afficher le cadeau de participation avec un texte autour
        </label>
        {form.queue_show_gift && (
          <>
            <input style={inputLight} value={form.queue_gift_label} placeholder="ex: Pour participer"
              onChange={e => set(f => ({ ...f, queue_gift_label: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={inputLight} value={form.queue_gift_label_pos}
                onChange={e => set(f => ({ ...f, queue_gift_label_pos: e.target.value }))}>
                <option value="none">Texte masqué</option>
                <option value="above">Au-dessus</option>
                <option value="below">En dessous</option>
                <option value="left">A gauche</option>
                <option value="right">A droite</option>
              </select>
              <input type="number" style={inputLight} min={8} max={80} value={form.queue_gift_label_size}
                onChange={e => set(f => ({ ...f, queue_gift_label_size: Number(e.target.value) }))} />
              <input type="color" value={form.queue_gift_label_color}
                onChange={e => set(f => ({ ...f, queue_gift_label_color: e.target.value }))}
                style={{ width: 44, height: 34, padding: 2, border: '1px solid #e2e8f0', borderRadius: 6 }} />
            </div>
          </>
        )}
        <div style={{ color: '#a16207', fontSize: 11 }}>La liste, la zone de tirage et le cadeau se positionnent dans la prévisualisation plein écran.</div>
      </div>
    </div>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 24, width: 440, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 16 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function BannerFields({ form, set }: {
  form: AnimFormState;
  set: React.Dispatch<React.SetStateAction<AnimFormState>>;
}) {
  const pretty = (() => {
    if (form.text_content?.trim()) return form.text_content;
    return defaultBannerJson();
  })();
  const valid = (() => {
    try {
      const parsed = JSON.parse(pretty);
      return Array.isArray(parsed.patterns) && parsed.patterns.length > 0;
    } catch {
      return false;
    }
  })();
  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>🪧 Bannière</div>
      <div style={{ color: '#64748b', fontSize: 11, lineHeight: 1.45 }}>
        La bannière reste active pendant le live. Les streamers peuvent modifier le texte, les couleurs, la police et le type de défilement dans Mes lives interactifs.
      </div>
      <LightField label="Configuration par défaut">
        <textarea
          style={{ ...inputLight, minHeight: 170, resize: 'vertical', fontFamily: 'Consolas, monospace', fontSize: 12 }}
          value={pretty}
          onChange={e => set(f => ({ ...f, text_content: e.target.value }))}
        />
      </LightField>
      <div style={{ color: valid ? '#16a34a' : '#dc2626', fontSize: 11, fontWeight: 700 }}>
        {valid ? 'Configuration valide' : 'JSON invalide ou aucune étape définie'}
      </div>
      <div style={{ color: '#64748b', fontSize: 11 }}>
        Effets disponibles : scroll-left, scroll-right, scroll-up, scroll-down, typewriter, fade. La position et la taille se règlent dans la prévisualisation.
      </div>
    </div>
  );
}

function LightField({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, ...style }}>
      <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputLight: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
const btnPrimary: React.CSSProperties = { padding: '7px 14px', borderRadius: 7, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' };
const btnSecondary: React.CSSProperties = { padding: '7px 12px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 16px', borderRadius: 7, border: '1px solid #e2e8f0', background: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', padding: '4px 7px', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center' };
const posBadge: React.CSSProperties = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 5px', fontSize: 10, color: '#64748b', fontFamily: 'monospace' };
const dropdownItem: React.CSSProperties = { display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9', color: '#0f172a', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };


